// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loader2, ArrowUpRight, ArrowLeft, X, ChevronDown, ChevronRight, Pause, Play, Square, Trash2, Plus, Github } from 'lucide-react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement,
  Tooltip as CjsTooltip, Legend as CjsLegend, Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { getCommandDeck, getDeckMachineSurface, sendDeckMachineWrite, sendDeckCapabilitiesImport, sendDeckCapabilitiesZipImport, deleteDeckMachine, requestAgentBridgeUrl, sendProjectControl, getProjectDeleteManifest, deleteBuilderProject } from '../../services/api';
import { aaiApi } from '../../services/aaiApi';
import { ActionUpdateDrawer } from '../champion/ActionUpdateDrawer';
import { isClosed, statusOf } from '../champion/actionStatus';
import CompanyUsageTab from './CompanyUsageTab';
import AiCostMarginTab from './AiCostMarginTab';
// import HostingTab from './HostingTab';
import AgentIncidentsTab from './AgentIncidentsTab';
import LiveTab from './LiveTab';
import MachineSettingsTab, { MACHINE_SETTINGS_TABS } from './machine-settings/MachineSettingsTab';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, CjsTooltip, CjsLegend, Filler);

/**
 * The Command Deck — the platform owner's one surface, as two consoles.
 *
 *   Business = the company: revenue, margin, accounts, growth, our team.
 *   Machine  = the engine: cost, delivery, providers, routing, compute.
 *
 * Built to the Agents Anywhere system (IBM Plex, blue-tinted cards, mono
 * eyebrows, big numerals) and self-contained light/dark — the deck is its own
 * room, synced to the shell header toggle via `aa_theme`.
 *
 * The honesty system: every figure carries a confidence tag —
 *   live       = computed today from real tables
 *   est        = directional, has a known caveat (named on Data Health)
 *   needs data = an honest blank; the fix is named, not faked
 * so the deck never shows a number it can't stand behind.
 */

const usd = (n) => n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const usd2 = (n) => n == null ? '—' : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ms = (n) => n == null ? '—' : `${Number(n).toLocaleString()}ms`;
const prob = (n) => n == null ? '—' : Number(n).toFixed(3);
const pct = (n) => n == null ? '—' : `${Number(n).toFixed(0)}%`;

const DECK_CSS = `
.cd-root{
  --cd-bg:#f7f7f5; --cd-panel:#ffffff; --cd-card:#eef3ff; --cd-ink:#18191d;
  --cd-muted:#666971; --cd-mut2:#92949d; --cd-line:#e7e7e4; --cd-line2:#dcdcd8;
  --cd-blue:#18191d; --cd-blue-ink:#18191d; --cd-blue-soft:rgba(24,25,29,.07);
  --cd-good:#2f9e57; --cd-good-soft:rgba(47,158,87,.12);
  --cd-warn:#c77d13; --cd-warn-soft:rgba(199,125,19,.13);
  --cd-red:#d63a53; --cd-red-soft:rgba(214,58,83,.12);
  --cd-purple:#8154d1; --cd-purple-soft:rgba(129,84,209,.12);
  background:var(--cd-bg); color:var(--cd-ink);
  font-family:'IBM Plex Sans',system-ui,sans-serif;
  margin:-1.5rem -1.5rem -2rem; padding:1.5rem 1.5rem 3rem; min-height:calc(100vh - 3.5rem);
}
@media(min-width:1024px){.cd-root{margin:-2rem -2rem -2rem; padding:1.75rem 2rem 3rem;}}
/* A phone: the shell pads the page by 1rem, so the deck cancels 1rem. Wide
   tables scroll inside their panel rather than being cut off by it. */
@media(max-width:639px){
  .cd-root{margin:-1rem -1rem -2rem; padding:1rem 1rem 3rem;}
  .cd-root .cd-panel{overflow-x:auto}
  .cd-root .cd-sel{max-width:100%}
  .cd-root .cd-ph{flex-wrap:wrap}
  .cd-root .cd-link{white-space:nowrap}
}
.cd-root[data-cd-theme="dark"]{
  --cd-bg:#131620; --cd-panel:#1c2130; --cd-card:#212a42; --cd-ink:#eef2fb;
  --cd-muted:#9aa5bd; --cd-mut2:#6b7690; --cd-line:#2a3144; --cd-line2:#39415a;
  --cd-blue:#6ea0ff; --cd-blue-ink:#8fb6ff; --cd-blue-soft:rgba(110,160,255,.15);
  --cd-good:#6ee7a8; --cd-good-soft:rgba(110,231,168,.14);
  --cd-warn:#f2c073; --cd-warn-soft:rgba(242,192,115,.15);
  --cd-red:#fca5a5; --cd-red-soft:rgba(252,165,165,.14);
  --cd-purple:#c4a0f5; --cd-purple-soft:rgba(196,160,245,.15);
}
.cd-eyebrow{font-family:'IBM Plex Sans',sans-serif;font-size:10px;letter-spacing:.13em;text-transform:uppercase;color:var(--cd-mut2);font-weight:500}
.cd-mono{font-family:'IBM Plex Sans',sans-serif;font-variant-numeric:tabular-nums}
.cd-h1{font-size:21px;font-weight:650;letter-spacing:-.02em;margin:3px 0 2px}
.cd-sub{color:var(--cd-muted);font-size:12.5px;max-width:64ch}
/* mode switch */
.cd-modes{display:inline-flex;background:var(--cd-panel);border:1px solid var(--cd-line);border-radius:10px;padding:3px;gap:2px}
.cd-mode{border:0;background:transparent;color:var(--cd-muted);font-family:inherit;font-size:12.5px;font-weight:600;cursor:pointer;padding:6px 15px;border-radius:7px;display:flex;align-items:center;gap:7px}
.cd-mode .dot{width:6px;height:6px;border-radius:50%;background:currentColor;opacity:.45}
.cd-mode[data-on="true"]{background:var(--cd-bg);color:var(--cd-ink);box-shadow:0 1px 3px rgba(0,0,0,.12)}
.cd-mode[data-on="true"][data-m="business"]{color:var(--cd-blue-ink)}
.cd-mode[data-on="true"] .dot{opacity:1}
/* tabs */
.cd-tabs{display:flex;gap:2px;overflow-x:auto;border-bottom:1px solid var(--cd-line);margin:16px 0 18px;padding-bottom:0}
.cd-tab{white-space:nowrap;border:0;background:transparent;color:var(--cd-muted);font-family:inherit;font-size:12.5px;font-weight:500;cursor:pointer;padding:9px 13px;border-bottom:2px solid transparent;margin-bottom:-1px}
.cd-tab:hover{color:var(--cd-ink)}
.cd-tab[data-on="true"]{color:var(--cd-ink);border-bottom-color:var(--cd-blue);font-weight:600}
.cd-tab .tg{margin-left:6px;font-family:'IBM Plex Sans',sans-serif;font-size:9px;padding:1px 5px;border-radius:20px;background:var(--cd-red-soft);color:var(--cd-red)}
/* status tags */
.cd-st{font-family:'IBM Plex Sans',sans-serif;font-size:9px;letter-spacing:.07em;text-transform:uppercase;padding:2px 6px;border-radius:5px;font-weight:500;white-space:nowrap}
.cd-st.live{background:var(--cd-good-soft);color:var(--cd-good)}
.cd-st.est{background:var(--cd-warn-soft);color:var(--cd-warn)}
.cd-st.gap{background:var(--cd-blue-soft);color:var(--cd-blue-ink);border:1px dashed var(--cd-blue)}
.cd-legend{display:flex;gap:14px;flex-wrap:wrap;align-items:center;font-size:11.5px;color:var(--cd-muted);margin-bottom:14px}
/* alarms */
.cd-alarms{display:flex;gap:9px;flex-wrap:wrap;margin-bottom:16px}
.cd-al{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:500;padding:7px 11px;border-radius:9px}
.cd-al .d{width:7px;height:7px;border-radius:50%}
.cd-al.red{background:var(--cd-red-soft);color:var(--cd-red)}.cd-al.red .d{background:var(--cd-red)}
.cd-al.warn{background:var(--cd-warn-soft);color:var(--cd-warn)}.cd-al.warn .d{background:var(--cd-warn)}
.cd-al.good{background:var(--cd-good-soft);color:var(--cd-good)}.cd-al.good .d{background:var(--cd-good)}
/* kpi */
.cd-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:13px;margin-bottom:18px}
@media(max-width:900px){.cd-kpis{grid-template-columns:repeat(2,1fr)}}
.cd-kpi{background:var(--cd-panel);border:1px solid var(--cd-line);border-radius:12px;padding:14px 15px}
.cd-kpi .lab{display:flex;align-items:center;gap:6px;justify-content:space-between}
.cd-kpi .v{font-family:'IBM Plex Sans',sans-serif;font-variant-numeric:tabular-nums;font-size:25px;font-weight:600;letter-spacing:-.02em;margin:9px 0 2px}
.cd-kpi .v.accent{color:var(--cd-blue-ink)}.cd-kpi .v.good{color:var(--cd-good)}.cd-kpi .v.red{color:var(--cd-red)}
.cd-kpi .s{font-size:11px;color:var(--cd-mut2)}
.cd-kpi .fx{font-family:'IBM Plex Sans',sans-serif;font-size:9.5px;color:var(--cd-mut2);margin-top:5px;border-top:1px dashed var(--cd-line);padding-top:5px}
/* grids + panels */
.cd-grid2{display:grid;grid-template-columns:1.6fr 1fr;gap:14px;align-items:start}
.cd-grid2.even{grid-template-columns:1fr 1fr}
@media(max-width:900px){.cd-grid2,.cd-grid2.even{grid-template-columns:1fr}}
.cd-panel{background:var(--cd-panel);border:1px solid var(--cd-line);border-radius:12px;overflow:hidden;margin-bottom:14px}
.cd-ph{display:flex;align-items:center;justify-content:space-between;padding:12px 15px;border-bottom:1px solid var(--cd-line);gap:10px}
.cd-ph .t{font-weight:600;font-size:13.5px;display:flex;align-items:center;gap:8px}
.cd-cap{padding:9px 15px;font-family:'IBM Plex Sans',sans-serif;font-size:10px;color:var(--cd-mut2);border-top:1px solid var(--cd-line);background:var(--cd-bg)}
.cd-link{color:var(--cd-blue-ink);font-weight:600;font-size:12px;cursor:pointer;background:none;border:0;display:inline-flex;align-items:center;gap:4px}
/* table */
.cd-t{width:100%;border-collapse:collapse}
.cd-t th{text-align:left;font-family:'IBM Plex Sans',sans-serif;font-size:9.5px;letter-spacing:.09em;text-transform:uppercase;color:var(--cd-mut2);font-weight:500;padding:9px 15px;border-bottom:1px solid var(--cd-line)}
.cd-t th.r,.cd-t td.r{text-align:right}
.cd-t td{padding:10px 15px;border-bottom:1px solid var(--cd-line);font-size:12.5px}
.cd-t tr:last-child td{border-bottom:0}
.cd-t tbody tr.cl{cursor:pointer}
.cd-t tbody tr:hover{background:var(--cd-bg)}
.cd-t td .m{font-family:'IBM Plex Sans',sans-serif;font-variant-numeric:tabular-nums}
.cd-sub2{color:var(--cd-mut2);font-size:11px}
.cd-chip{font-family:'IBM Plex Sans',sans-serif;font-size:10px;padding:2px 8px;border-radius:20px;display:inline-flex;align-items:center;gap:5px}
.cd-chip.g{background:var(--cd-good-soft);color:var(--cd-good)}.cd-chip.o{background:var(--cd-bg);color:var(--cd-mut2);border:1px solid var(--cd-line)}
.cd-chip.w{background:var(--cd-warn-soft);color:var(--cd-warn)}.cd-chip.b{background:var(--cd-blue-soft);color:var(--cd-blue-ink)}.cd-chip.p{background:var(--cd-purple-soft);color:var(--cd-purple)}
.cd-chip.r{background:var(--cd-red-soft);color:var(--cd-red)}
.cd-hbar.g{background:var(--cd-good)}.cd-hbar.b{background:var(--cd-blue-ink)}.cd-hbar.w{background:var(--cd-warn)}
.cd-hbar.r{background:var(--cd-red)}.cd-hbar.p{background:var(--cd-purple)}.cd-hbar.o{background:var(--cd-mut2)}
/* charts */
.cd-charts{display:grid;grid-template-columns:repeat(4,1fr);gap:13px;margin-bottom:14px}
@media(max-width:1100px){.cd-charts{grid-template-columns:repeat(2,1fr)}}
@media(max-width:640px){.cd-charts{grid-template-columns:1fr}}
.cd-chart-body{height:150px;padding:8px 12px 10px}
/* filter row */
.cd-filters{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:12px}
.cd-fchip{font-family:inherit;font-size:11.5px;font-weight:600;border:1px solid var(--cd-line2);background:var(--cd-panel);color:var(--cd-muted);border-radius:20px;padding:5px 12px;cursor:pointer}
.cd-fchip[data-on="true"]{background:var(--cd-blue);border-color:var(--cd-blue);color:#fff}
.cd-finput{font-family:inherit;font-size:12px;border:1px solid var(--cd-line2);background:var(--cd-panel);color:var(--cd-ink);border-radius:8px;padding:6px 11px;min-width:210px}
.cd-finput::placeholder{color:var(--cd-mut2)}
.cd-hd{width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:6px}
.cd-hd.g{background:var(--cd-good)}.cd-hd.r{background:var(--cd-red)}.cd-hd.a{background:var(--cd-warn)}
.cd-flag{font-family:'IBM Plex Sans',sans-serif;font-size:9.5px;color:var(--cd-warn);background:var(--cd-warn-soft);padding:2px 6px;border-radius:5px;margin-left:6px}
.cd-mbar{height:6px;border-radius:20px;background:var(--cd-line);overflow:hidden;min-width:56px}
.cd-mbar>i{display:block;height:100%;border-radius:20px}
.cd-sel{font-family:inherit;font-size:12px;font-weight:500;border:1px solid var(--cd-line2);background:var(--cd-bg);color:var(--cd-ink);border-radius:7px;padding:5px 9px;cursor:pointer;display:inline-flex;align-items:center;gap:7px}
.cd-sel.warn{border-color:var(--cd-warn);color:var(--cd-warn);background:var(--cd-warn-soft)}
.cd-note{background:var(--cd-card);border:1px solid var(--cd-blue-soft);border-radius:12px;padding:14px 16px;font-size:12.5px;color:var(--cd-muted);margin-bottom:14px}
.cd-note b{color:var(--cd-ink)}
.cd-btn{font-family:inherit;font-size:12px;font-weight:600;border-radius:8px;padding:8px 13px;cursor:pointer;border:1px solid var(--cd-line2);background:var(--cd-panel);color:var(--cd-ink);display:inline-flex;gap:6px;align-items:center}
.cd-btn:hover{border-color:var(--cd-blue);color:var(--cd-blue-ink)}
.cd-btn.pri{background:var(--cd-blue);border-color:var(--cd-blue);color:#fff}
.cd-btn.sm{padding:5px 10px;font-size:11.5px}
/* drawer */
.cd-drawer-scrim{position:fixed;inset:0;background:rgba(8,10,18,.45);z-index:80}
.cd-drawer{position:fixed;top:0;right:0;height:100%;width:min(460px,92vw);background:var(--cd-panel);border-left:1px solid var(--cd-line);box-shadow:-16px 0 48px rgba(0,0,0,.25);overflow-y:auto;padding:22px;z-index:81}
.cd-detail-head{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:18px}
.cd-detail-title{font-size:25px;font-weight:650;letter-spacing:-.025em;margin:7px 0 4px}
.cd-detail-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:10px}
.cd-detail-grid{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(300px,.65fr);gap:14px;align-items:start}
@media(max-width:900px){.cd-detail-head{flex-direction:column}.cd-detail-grid{grid-template-columns:1fr}}
.cd-foot{margin-top:20px;font-size:11px;color:var(--cd-mut2)}
/* form fields (capabilities create/import drawers) */
.cd-label{display:block;font-size:11px;font-weight:600;color:var(--cd-muted);margin:12px 0 5px}
.cd-label:first-child{margin-top:0}
.cd-input,.cd-textarea,.cd-select{width:100%;font-family:inherit;font-size:13px;color:var(--cd-ink);background:var(--cd-bg);border:1px solid var(--cd-line2);border-radius:8px;padding:8px 10px}
.cd-input:focus,.cd-textarea:focus,.cd-select:focus{outline:none;border-color:var(--cd-blue)}
.cd-textarea{resize:vertical;min-height:90px;font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:12px}
.cd-check-row{display:flex;align-items:center;gap:8px;padding:7px 0;border-bottom:1px solid var(--cd-line)}
.cd-check-row:last-child{border-bottom:0}
`;

const St = ({ k }) => <span className={`cd-st ${k}`}>{k === 'gap' ? 'needs data' : k}</span>;

const Kpi = ({ eyebrow, tag, value, vclass, sub, fx }) => (
  <div className="cd-kpi">
    <div className="lab"><span className="cd-eyebrow">{eyebrow}</span>{tag && <St k={tag} />}</div>
    <div className={`v ${vclass || ''}`}>{value}</div>
    {sub && <div className="s">{sub}</div>}
    {fx && <div className="fx">{fx}</div>}
  </div>
);

const Panel = ({ title, tag, right, children, cap }) => (
  <div className="cd-panel">
    <div className="cd-ph"><span className="t">{title}{tag && <St k={tag} />}</span>{right}</div>
    {children}
    {cap && <div className="cd-cap">{cap}</div>}
  </div>
);

// Loading / network-error wrapper for the lazily-fetched Machine admin
// surfaces. `children` is a render-prop receiving the payload.
const SurfaceShell = ({ st, children }) => {
  if (!st || st.loading) return (
    <div style={{ display: 'grid', placeItems: 'center', padding: 60 }}><Loader2 className="animate-spin" style={{ color: 'var(--cd-mut2)' }} /></div>
  );
  if (st.error) return (
    <div className="cd-note"><b>Couldn’t load this surface.</b> {st.error}</div>
  );
  return children(st.data);
};

// Honest state when the builder passthrough itself is down — the deck's rule:
// name the failure, never render a fake blank.
const BuilderDown = ({ reason }) => (
  <div className="cd-note"><b>Builder unreachable</b> ({reason || 'unknown'}). This page reads the builder admin surface through the service-key passthrough — it appears once <span className="cd-mono">AGENT_BUILDER_API_URL</span> + provisioning key resolve.</div>
);

// deck tab → aibot proxy path segment (GET /api/super/machine/…)
const MACHINE_SURFACES = { people: 'experts', caps: 'capabilities', platform: 'platform', audit: 'audit', projects: 'projects', cost: 'controls', margin: 'controls', swarm: 'jev-swarm-summary', compass: 'jev-swarm-summary' };


// The platform brake — kill switch + 30d execution spend cap, audited writes.

// The margin dial — one audited number (0–500%). Builder stores it in the
// cost-controls singleton; every flip carries the actor's name.
const MarginDial = ({ st, onWrite }) => {
  const ctr = st?.data?.controls || {};
  const [val, setVal] = useState(String(ctr.platformMarginPercent ?? 0));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(String(ctr.platformMarginPercent ?? 0)); }, [ctr.platformMarginPercent]);
  if (!st || st.loading) return <Panel title="Set the margin" tag="live"><div style={{ padding: 20, display: 'grid', placeItems: 'center' }}><Loader2 className="animate-spin" size={16} style={{ color: 'var(--cd-mut2)' }} /></div></Panel>;
  if (st.error || st.data?.available === false) return <Panel title="Set the margin" tag="gap"><div style={{ padding: '12px 15px', fontSize: 12.5, color: 'var(--cd-muted)' }}>Builder unreachable ({st.error || st.data?.reason}) — the dial appears when the passthrough resolves.</div></Panel>;
  return (
    <Panel title="Set the margin" tag="live" cap="Audited with your name. Takes effect within 60s — no redeploy. 0 = sell at cost.">
      <div style={{ padding: '10px 15px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <input className="cd-finput" style={{ maxWidth: 110 }} type="number" min="0" max="500" step="1"
          value={val} onChange={(e) => setVal(e.target.value)} />
        <span className="cd-sub2">percent on top of true cost</span>
        <button className="cd-btn pri sm" disabled={busy || val === '' || Number(val) < 0 || Number(val) > 500}
          onClick={async () => { setBusy(true); try { await onWrite({ platformMarginPercent: Number(val) }); } finally { setBusy(false); } }}>
          Save
        </button>
      </div>
      {ctr.updatedBy && <div className="cd-sub2" style={{ padding: '0 15px 10px' }}>last set by {ctr.updatedBy}{ctr.updatedAt ? ` · ${new Date(ctr.updatedAt).toLocaleString()}` : ''}</div>}
    </Panel>
  );
};

const ControlsPanel = ({ st, onWrite }) => {
  const c = st?.data;
  const ctr = c?.controls || {};
  const [cap, setCap] = useState(ctr.executionCapUsd30d ?? '');
  const [busy, setBusy] = useState(false);
  if (!st || st.loading) return (
    <Panel title="Controls" tag="live"><div style={{ padding: 20, display: 'grid', placeItems: 'center' }}><Loader2 className="animate-spin" size={16} style={{ color: 'var(--cd-mut2)' }} /></div></Panel>
  );
  if (st.error || c?.available === false) return (
    <Panel title="Controls" tag="gap"><div style={{ padding: '12px 15px', fontSize: 12.5, color: 'var(--cd-muted)' }}>Builder unreachable ({st.error || c?.reason}) — controls appear when the passthrough resolves.</div></Panel>
  );
  const act = async (body) => { setBusy(true); try { await onWrite(body); } finally { setBusy(false); } };
  return (
    <Panel title="Controls" tag="live"
      cap="Kill switch stops NEW runs at every entry surface (≤60s cache). Cap = 30d trusted LLM spend; breach pauses new runs + posts COST_ALERT. Every flip is audited with your name.">
      <div style={{ padding: '6px 15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--cd-line)' }}>
          <span>30d trusted spend</span>
          <span className="cd-mono" style={{ color: c.capBreached ? 'var(--cd-red)' : undefined }}>
            {usd2(c.spend30dUsd)}{ctr.executionCapUsd30d != null ? ` / ${usd(ctr.executionCapUsd30d)}` : ''}
            {c.capBreached && <span className="cd-chip r" style={{ marginLeft: 8 }}>cap breached</span>}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--cd-line)', gap: 10 }}>
          <span>Spend cap · execution</span>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input className="cd-finput" style={{ minWidth: 90, maxWidth: 110 }} type="number" min="0" placeholder="none"
              value={cap} onChange={(e) => setCap(e.target.value)} />
            <button className="cd-btn sm" disabled={busy}
              onClick={() => act({ executionCapUsd30d: cap === '' ? null : Number(cap) })}>Set</button>
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0' }}>
          <span>Kill switch <span className="cd-sub2">stops all new runs</span></span>
          <button className={`cd-btn sm${ctr.killSwitch ? '' : ' pri'}`} disabled={busy}
            style={ctr.killSwitch ? { borderColor: 'var(--cd-good)', color: 'var(--cd-good)' } : { background: 'var(--cd-red)', borderColor: 'var(--cd-red)' }}
            onClick={() => {
              const engaging = !ctr.killSwitch;
              if (window.confirm(engaging ? 'Engage the kill switch? All NEW runs pause platform-wide within 60s.' : 'Release the kill switch and resume new runs?')) {
                act({ killSwitch: engaging });
              }
            }}>
            {ctr.killSwitch ? 'Release — runs paused' : 'Engage kill switch'}
          </button>
        </div>
      </div>
    </Panel>
  );
};

// Chart palette — validated with the dataviz six-checks per mode (categorical
// pair passes CVD + normal-vision separation on the deck's panel surfaces).
const CHART_THEME = {
  light: { s1: '#4080f2', s2: '#c77d13', grid: '#e4e8f0', tick: '#8a90a0' },
  dark: { s1: '#5b90f5', s2: '#b3761a', grid: '#2a3144', tick: '#6b7690' },
};
const MONO = "'IBM Plex Sans', sans-serif";

const chartOpts = (t, { legend = false, stacked = false, money = false } = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: legend
      ? { display: true, position: 'bottom', labels: { color: t.tick, boxWidth: 8, boxHeight: 8, font: { family: MONO, size: 9 } } }
      : { display: false },
    tooltip: money
      ? { callbacks: { label: (c) => `${c.dataset.label ? `${c.dataset.label}: ` : ''}$${Number(c.parsed.y).toFixed(2)}` } }
      : {},
  },
  scales: {
    x: {
      stacked,
      grid: { display: false },
      border: { color: t.grid },
      ticks: { color: t.tick, font: { family: MONO, size: 8.5 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
    },
    y: {
      stacked,
      beginAtZero: true,
      grid: { color: t.grid },
      border: { display: false },
      ticks: { color: t.tick, font: { family: MONO, size: 8.5 }, maxTicksLimit: 4, ...(money ? { callback: (v) => `$${v}` } : {}) },
    },
  },
});

const ago = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

// Verdict → chip class + label. Status colors carry state, per the deck system.
const STUCK_META = {
  needs_customer: { label: 'needs customer', cls: 'w' },
  needs_expert: { label: 'needs expert', cls: 'r' },
  erroring: { label: 'erroring', cls: 'r' },
  stalled: { label: 'stalled', cls: 'w' },
  unassigned: { label: 'unassigned', cls: 'p' },
  shipped: { label: 'shipped', cls: 'g' },
  moving: { label: 'moving', cls: 'b' },
  checking: { label: 'checking', cls: 'b' },
  review: { label: 'ready for review', cls: 'p' },
  ready: { label: 'ready to ship', cls: 'g' },
};
const STUCK_STATES = ['needs_customer', 'needs_expert', 'erroring', 'stalled', 'unassigned'];

/**
 * The Projects lens — every project: whose client, whose hands, what it costs,
 * what shipped, and a stuck verdict with cause + since-when. Charts read the
 * builder's aligned weekly series; the roster is filterable; a row opens a
 * linkable project page with build stats, costs and the loop requests on it.
 */
const ProjectsLens = ({ d, theme, openBuilder, projectId, openProject, closeProject }) => {
  const t = CHART_THEME[theme] || CHART_THEME.light;
  const [flt, setFlt] = useState('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [judgments, setJudgments] = useState(null);
  // Per-project super controls (2026-10-11): pause / stop builds / clean delete.
  const [controls, setControls] = useState(null);
  const [acting, setActing] = useState(false);
  const [deleteManifest, setDeleteManifest] = useState(null);
  const [deleted, setDeleted] = useState(false);

  // Row-aware controls: the same three verbs work from the roster's Actions
  // column and from the detail page. `overrides` carries fresh paused/running
  // truth for roster rows (the parent payload only refreshes on the deck poll);
  // `deletedIds` hides rows the super just deleted.
  const [overrides, setOverrides] = useState({});
  const [deletedIds, setDeletedIds] = useState(() => new Set());

  const projects = d.projects || [];
  const sel = projectId ? projects.find((p) => p.id === projectId) : null;
  const totals = d.totals || {};
  const s = d.series || {};
  const weeks = (s.weeks || []).map((w) => w.slice(5).replace('-', '/'));

  const filtered = projects.filter((p) => {
    if (deletedIds.has(p.id)) return false;
    const state = p.stuck?.state;
    if (flt === 'running' && (p.running?.building || 0) + (p.running?.checking || 0) === 0) return false;
    if (flt === 'paused' && !p.paused) return false;
    if (flt === 'stuck' && !STUCK_STATES.includes(state)) return false;
    if (flt !== 'all' && !['stuck', 'running', 'paused'].includes(flt) && state !== flt) return false;
    if (q) {
      const hay = `${p.title} ${p.agent_name || ''} ${p.client?.name || ''} ${p.client?.email || ''} ${p.expert?.name || ''} ${p.expert?.email || ''}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  useEffect(() => {
    if (!projectId) {
      setDetail(null);
      setJudgments(null);
      return;
    }
    setDetail({ loading: true });
    getDeckMachineSurface(`projects/${projectId}`)
      .then((data) => setDetail({ data }))
      .catch((e) => setDetail({ error: e?.response?.data?.detail || 'request failed' }));
    setJudgments({ loading: true });
    getDeckMachineSurface('jev-judgments', { project_id: projectId, limit: '50' })
      .then((data) => setJudgments({ data }))
      .catch((e) => setJudgments({ error: e?.response?.data?.detail || 'request failed' }));
    setControls(null); setDeleteManifest(null); setDeleted(false);
    getDeckMachineSurface(`projects/${projectId}/controls`)
      .then((data) => setControls(data))
      .catch(() => setControls(null));
  }, [projectId]);

  const doControl = async (id, action) => {
    if (acting) return;
    setActing(true);
    try {
      const res = await sendProjectControl(id, action);
      if (id === projectId) setControls(res);
      setOverrides((o) => ({ ...o, [id]: { paused: !!res.paused, running: { building: 0, checking: 0, ...(res.running?.length ? { building: res.running.length } : {}) } } }));
      toast.success(action === 'pause' ? 'Project paused — nothing will build until resume.'
        : action === 'resume' ? 'Project resumed.'
        : 'Running builds stopped.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally { setActing(false); }
  };

  const askDelete = async (id) => {
    if (acting) return;
    setActing(true);
    try { setDeleteManifest(await getProjectDeleteManifest(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || 'could not read the delete manifest'); }
    finally { setActing(false); }
  };

  const doDelete = async () => {
    if (acting || !deleteManifest?.confirmToken) return;
    setActing(true);
    const id = deleteManifest.projectId;
    try {
      await deleteBuilderProject(id, deleteManifest.confirmToken);
      setDeleteManifest(null);
      setDeletedIds((s) => new Set([...s, id]));
      if (id === projectId) setDeleted(true);
      toast.success('Project deleted — builds stopped, repos archived, sandboxes killed, entries removed on both sides.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'delete failed');
    } finally { setActing(false); }
  };

  const bs = detail?.data?.build_stats;
  const costs = detail?.data?.costs;

  // A centered modal, not an inline note: the Delete button lives on rows deep
  // in the roster, and a note rendered at the top of the page read as "the
  // button did nothing" (CTO, 10-12).
  const deleteConfirmNote = deleteManifest ? (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(17, 20, 24, 0.45)', display: 'grid', placeItems: 'center' }}
      onClick={() => !acting && setDeleteManifest(null)}
    >
      <div
        style={{ width: 'min(480px, calc(100vw - 32px))', background: '#fff', border: '1px solid var(--cd-line, #e5e2da)', borderRadius: 14, boxShadow: '0 20px 60px rgba(17,20,24,.25)', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--cd-line, #e5e2da)' }}>
          <span style={{ display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 9, background: '#fdf0ee', color: '#b42318' }}><Trash2 size={15} /></span>
          <div>
            <div className="cd-eyebrow">Agents Anywhere · permanent action</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 1 }}>Delete “{deleteManifest.title}”?</div>
          </div>
        </div>
        <div style={{ padding: '14px 18px', fontSize: 13, lineHeight: 1.5 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
            {[[`${deleteManifest.tickets}`, `work item${deleteManifest.tickets === 1 ? '' : 's'} removed`],
              [`${deleteManifest.artifacts}`, `artifact${deleteManifest.artifacts === 1 ? '' : 's'} removed`],
              [`${deleteManifest.conversations}`, `conversation${deleteManifest.conversations === 1 ? '' : 's'} removed`],
              [`${deleteManifest.sandboxIds?.length || 0}`, `sandbox${(deleteManifest.sandboxIds?.length || 0) === 1 ? '' : 'es'} killed`],
              [`${deleteManifest.managedRepos?.length || 0}`, `repo${(deleteManifest.managedRepos?.length || 0) === 1 ? '' : 's'} archived — recoverable`],
              [`${deleteManifest.running || 0}`, 'running builds stopped first'],
            ].map(([n, label], i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <span className="m" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
                <span className="cd-sub2">{label}</span>
              </div>
            ))}
          </div>
          <div className="cd-sub2" style={{ marginTop: 10 }}>
            Removed on the builder AND here. The archived repos are the only part that can be brought back.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--cd-line, #e5e2da)', background: '#faf9f6' }}>
          <button className="cd-btn" disabled={acting} onClick={() => setDeleteManifest(null)}>Cancel</button>
          <button className="cd-btn" disabled={acting} style={{ color: '#fff', background: '#b42318', borderColor: '#b42318' }} onClick={doDelete}>{acting ? 'Deleting…' : 'Delete it for good'}</button>
        </div>
      </div>
    </div>
  ) : null;

  if (projectId && !sel) return (
    <>
      <button className="cd-btn" onClick={closeProject}><ArrowLeft size={14} /> All projects</button>
      <div className="cd-note" style={{ marginTop: 14 }}><b>Project unavailable.</b> It may have been removed or may no longer be visible to this account.</div>
    </>
  );

  if (sel && deleted) return (
    <>
      <button className="cd-btn" onClick={closeProject}><ArrowLeft size={14} /> All projects</button>
      <div className="cd-note" style={{ marginTop: 14 }}><b>“{sel.title}” was deleted.</b> Builds were stopped, the managed repos were archived (recoverable), the sandboxes were killed and the entries were removed on both sides. The roster updates on the next deck refresh.</div>
    </>
  );

  if (sel) return (
    <>
      <div className="cd-detail-head">
        <div>
          <button className="cd-link" onClick={closeProject}><ArrowLeft size={14} /> All projects</button>
          <div className="cd-eyebrow" style={{ marginTop: 18 }}>Project details</div>
          <h1 className="cd-detail-title">{sel.title}</h1>
          <div className="cd-sub">{sel.client?.name || sel.client?.email || 'Unnamed client'} · {sel.expert?.name || 'Unassigned'}</div>
          <div className="cd-detail-meta">
            <span className={`cd-chip ${(STUCK_META[sel.stuck?.state] || STUCK_META.moving).cls}`}>{(STUCK_META[sel.stuck?.state] || STUCK_META.moving).label}</span>
            {sel.stuck?.reason && <span className="cd-sub2">{sel.stuck.reason}</span>}
            {sel.errors_7d > 0 && sel.unresolved_errors === 0 && <span className="cd-chip o">{sel.errors_7d} recovered incident{sel.errors_7d === 1 ? '' : 's'} in 7d</span>}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <button className="cd-btn pri" onClick={() => openBuilder(sel.workspace_id)}>Open in builder <ArrowUpRight size={14} /></button>
          <div style={{ display: 'flex', gap: 8 }}>
            {controls?.paused
              ? <button className="cd-btn" disabled={acting} onClick={() => doControl(projectId, 'resume')}>Resume project</button>
              : <button className="cd-btn" disabled={acting} onClick={() => doControl(projectId, 'pause')}>Pause project</button>}
            <button className="cd-btn" disabled={acting || !(controls?.running?.length)} onClick={() => doControl(projectId, 'stop_builds')}>
              Stop builds{controls?.running?.length ? ` (${controls.running.length})` : ''}
            </button>
            <button className="cd-btn" disabled={acting} style={{ color: '#b42318', borderColor: '#f3c0ba' }} onClick={() => askDelete(projectId)}>Delete…</button>
          </div>
          {controls?.paused && (
            <span className="cd-chip w">paused{controls.pausedBy ? ` by ${controls.pausedBy}` : ''} — nothing builds until resume</span>
          )}
        </div>
      </div>

      {deleteConfirmNote}

      {detail?.loading && <div style={{ display: 'grid', placeItems: 'center', padding: 80 }}><Loader2 className="animate-spin" style={{ color: 'var(--cd-mut2)' }} /></div>}
      {detail?.error && <div className="cd-note"><b>Couldn’t load the project details.</b> {detail.error}</div>}

      {detail?.data && (
        <>
          <div className="cd-kpis">
            <Kpi eyebrow="Total cost" value={usd2(costs?.ok !== false ? costs?.totalUsd : sel.cost?.total_usd)} />
            <Kpi eyebrow="LLM / Compute" value={`${usd2(costs?.llm?.totalUsd ?? sel.cost?.llm_usd)} · ${usd2(costs?.compute?.totalUsd ?? sel.cost?.compute_usd)}`} />
            <Kpi eyebrow="Autonomy" value={bs?.autonomy?.rate != null ? pct(bs.autonomy.rate * 100) : '—'} sub={bs ? `${bs.autonomy?.autonomous ?? 0} autonomous` : ''} />
            <Kpi eyebrow="Verification" value={bs?.verification ? `${bs.verification.acPassed}✓` : '—'} sub={bs?.verification?.acFailed ? `${bs.verification.acFailed} failed` : ''} />
          </div>

          {/* WHICH TICKET ATE WHAT (CTO 10-10) — the first question a super
              asks on a project. Ticket NAMES, dollars split by lane, the
              models that actually ran. Planner conversation is its own row. */}
          {costs?.llm?.byTicket?.length > 0 && (
            <Panel title="Spend by ticket" tag="live" cap="Every dollar attributed to the work item it built, checked or planned — from the per-call served-model receipts.">
              <div style={{ overflowX: 'auto' }}><table className="cd-t">
                <thead><tr><th>Work item</th><th className="r">Build</th><th className="r">QA</th><th className="r">Other</th><th className="r">Total</th><th className="r">Calls</th><th>Models</th></tr></thead>
                <tbody>{costs.llm.byTicket.map((t, i) => {
                  const build = (t.byLane?.execution || 0) + (t.byLane?.builder || 0);
                  const qa = (t.byLane?.qa || 0) + (t.byLane?.['qa-repair'] || 0);
                  const other = Math.max(0, (t.totalUsd || 0) - build - qa);
                  return (
                    <tr key={t.ticketId || `p${i}`}>
                      <td>{t.title}{t.status ? <div className="cd-sub2">{String(t.status).toLowerCase().replace(/_/g, ' ')}</div> : <div className="cd-sub2">conversation</div>}</td>
                      <td className="r m">{build ? usd2(build) : '—'}</td>
                      <td className="r m">{qa ? usd2(qa) : '—'}</td>
                      <td className="r m">{other >= 0.005 ? usd2(other) : '—'}</td>
                      <td className="r m"><b>{usd2(t.totalUsd)}</b></td>
                      <td className="r m">{t.calls}</td>
                      <td className="cd-sub2">{(t.models || []).join(', ')}</td>
                    </tr>
                  );
                })}</tbody>
              </table></div>
            </Panel>
          )}

          <div className="cd-detail-grid">
            <div>
              {costs?.llm?.byFeature?.length > 0 && (
                <Panel title="Spend by feature" tag="live">
                  <table className="cd-t"><tbody>
                    {costs.llm.byFeature.map((f, i) => <tr key={i}><td>{f.feature}</td><td className="r m">{usd2(f.costUsd)}</td></tr>)}
                    <tr><td><b>Compute ({costs.compute?.sandboxes ?? 0} boxes{costs.compute?.running ? ' · running' : ''})</b></td><td className="r m"><b>{usd2(costs.compute?.totalUsd)}</b></td></tr>
                  </tbody></table>
                </Panel>
              )}
              {bs?.escalationReasons?.length > 0 && (
                <Panel title="Why it needed humans" tag="live">
                  <table className="cd-t"><tbody>{bs.escalationReasons.slice(0, 6).map((r, i) => <tr key={i}><td>{r.label}</td><td className="r m">{r.count}</td></tr>)}</tbody></table>
                </Panel>
              )}
              {sel.loop_requests?.length > 0 && (
                <Panel title="The loop on this project" tag="live">
                  <table className="cd-t"><tbody>{sel.loop_requests.map((r) => (
                    <tr key={r.id}><td>{r.type}<div className="cd-sub2">{r.department || 'unrouted'} · {r.requester || '—'}</div></td><td className="r"><span className={`cd-chip ${r.status === 'assigned' ? 'b' : 'w'}`}>{r.status}</span><div className="cd-sub2">{ago(r.created_at)} ago{r.assignee ? ` · ${r.assignee}` : ''}</div></td></tr>
                  ))}</tbody></table>
                </Panel>
              )}
            </div>
            <Panel title="Delivery snapshot" tag="live">
              <table className="cd-t"><tbody>
                <tr><td>Phase</td><td className="r"><span className="cd-chip b">{(sel.phase || sel.project_status || 'unknown').toLowerCase()}</span></td></tr>
                <tr><td>Tickets</td><td className="r m">{sel.tickets?.completed ?? 0}/{sel.tickets?.total ?? 0}</td></tr>
                <tr><td>PRs merged</td><td className="r m">{sel.prs?.merged ?? 0}</td></tr>
                <tr><td>Lines changed</td><td className="r m">+{(sel.loc?.additions || 0).toLocaleString()} / −{(sel.loc?.deletions || 0).toLocaleString()}</td></tr>
                <tr><td>Last activity</td><td className="r m">{ago(sel.last_activity_at)} ago</td></tr>
              </tbody></table>
            </Panel>
          </div>
        </>
      )}

      {judgments?.loading && <div style={{ display: 'grid', placeItems: 'center', padding: 24 }}><Loader2 className="animate-spin" size={16} style={{ color: 'var(--cd-mut2)' }} /></div>}
      {judgments?.error && <div className="cd-note"><b>Couldn’t load judgments.</b> {judgments.error}</div>}
      {judgments?.data?.available === false && <div className="cd-note">Builder unreachable ({judgments.data.reason || 'unknown'}). Judgments appear when the connection recovers.</div>}
      {judgments?.data?.available !== false && judgments?.data && (() => {
        // DE-NOISED (CTO 10-10): the old table printed "Untitled ticket" on
        // every planner decision and four permanently-empty benchmark columns
        // (Compass p / comparison) that only shadow runs fill — unreadable.
        // Columns render only when at least one row carries the value; a
        // planner decision says what it IS.
        const rows = judgments.data.judgments || [];
        const hasCompassP = rows.some((j) => j.jev?.needs_flagship != null);
        const hasComparison = rows.some((j) => j.comparison?.model);
        return (
          <Panel title={`AA Compass decisions · ${rows.length}`} tag="live" cap="Each recorded routing step: which work it served and the model that actually ran.">
            <div style={{ overflowX: 'auto' }}><table className="cd-t">
              <thead><tr><th>Work</th><th>When</th>{hasCompassP && <th className="r">Compass p</th>}<th className="r">Compass latency</th>{hasComparison && <th>Comparison model</th>}{hasComparison && <th className="r">Comparison p</th>}<th>Ran on</th></tr></thead>
              <tbody>{rows.map((j) => (
                <tr key={j.id}>
                  <td>{j.ticket?.title || (j.lane === 'planner' ? 'Planner — conversation turn' : j.ticket?.id ? <span className="cd-sub2" style={{ fontStyle: 'italic' }}>Untitled ticket</span> : <span className="cd-sub2">platform step</span>)}<div className="cd-sub2">{j.lane}</div></td>
                  <td className="cd-sub2">{ago(j.created_at)} ago</td>
                  {hasCompassP && <td className="r m">{prob(j.jev?.needs_flagship)}</td>}
                  <td className="r m">{ms(j.jev?.latency_ms)}</td>
                  {hasComparison && <td className="cd-sub2">{shortModel(j.comparison?.model)}</td>}
                  {hasComparison && <td className="r m">{prob(j.comparison?.needs_flagship)}</td>}
                  <td className="cd-sub2">{shortModel(j.candidate_model)}</td>
                </tr>
              ))}{rows.length === 0 && <tr><td className="cd-sub2" colSpan={7}>No decisions recorded for this project yet.</td></tr>}</tbody>
            </table></div>
          </Panel>
        );
      })()}
      {sel.recent_error_events?.length > 0 && (
        <Panel title={`Recent machine incidents · ${sel.recent_error_events.length}`} tag={sel.unresolved_errors > 0 ? 'est' : 'live'}
          cap={sel.unresolved_errors > 0 ? 'At least one incident remains the latest operational signal for its work item.' : 'These incidents were followed by later progress or a successful QA/delivery signal. They remain here as history.'}>
          <table className="cd-t"><thead><tr><th>When</th><th>Work item</th><th>What happened</th><th>Status</th></tr></thead>
            <tbody>{sel.recent_error_events.map((event) => (
              <tr key={event.id}>
                <td className="cd-sub2 m">{ago(event.created_at)} ago</td>
                <td>{event.ticket_title || 'Project'}<div className="cd-sub2">{event.title}</div></td>
                <td>{event.description || 'No description recorded.'}</td>
                <td><span className={`cd-chip ${event.recovered ? 'g' : 'r'}`}>{event.recovered ? 'recovered' : 'unresolved'}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </Panel>
      )}
    </>
  );

  return (
    <>
      <div className="cd-kpis">
        <Kpi eyebrow="Projects" tag="live" value={totals.projects ?? 0} vclass="accent"
          sub={`${totals.active ?? 0} active · ${totals.shipped ?? 0} shipped`} fx="CANCELLED/REFUNDED excluded" />
        <Kpi eyebrow="Stuck now" tag="live" value={totals.stuck ?? 0} vclass={totals.stuck ? 'red' : 'good'}
          sub="needs human / erroring / stalled / unassigned" fx="verdict rules on each row" />
        <Kpi eyebrow="Spend · 30d" tag="live" value={usd2(totals.cost_30d_usd)} sub="LLM trusted + compute" fx="Σ per-project ledgers" />
        <Kpi eyebrow="PRs merged · 30d" tag="live" value={totals.prs_merged_30d ?? 0} vclass="good" fx="PullRequest MERGED by mergedAt" />
      </div>

      {deleteConfirmNote}
      <div className="cd-filters">
        {[['all', `All · ${projects.length}`],
          ['running', `Running now · ${projects.filter((p) => (p.running?.building || 0) + (p.running?.checking || 0) > 0).length}`],
          ['paused', `Paused · ${projects.filter((p) => p.paused).length}`],
          ['stuck', `Stuck · ${totals.stuck ?? 0}`],
          ['needs_customer', 'Needs customer'],
          ['needs_expert', 'Needs expert'],
          ['unassigned', 'Unassigned'],
          ['shipped', 'Shipped']].map(([k, label]) => (
          <button key={k} className="cd-fchip" data-on={flt === k} onClick={() => setFlt(k)}>{label}</button>
        ))}
        <input className="cd-finput" placeholder="Filter by client, expert, project…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Panel title={`Every project · ${filtered.length}`} tag="live"
        cap="Phase = PipelineState (Project.status is not progress). Cost = trusted LLM + compute per project. Verdict names its rule. Click a row for the full story.">
        <div style={{ overflowX: 'auto' }}>
          <table className="cd-t">
            <thead><tr>
              <th>Project</th><th>Client</th><th>Provider</th><th>Expert</th><th>Phase</th>
              <th className="r">Work</th>
              <th className="r">Cost</th><th className="r">Activity</th><th>Verdict</th><th className="r">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((p) => {
                const meta = STUCK_META[p.stuck?.state] || STUCK_META.moving;
                const paused = overrides[p.id]?.paused ?? p.paused;
                const run = overrides[p.id]?.running ?? p.running;
                const act = { padding: 4, lineHeight: 0, borderRadius: 7 };
                return (
                  <tr key={p.id} className="cl" onClick={() => openProject(p.id)}>
                    <td style={{ maxWidth: 260 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title || 'Untitled project'}</span>
                        {run?.building > 0 && <span className="cd-chip g">● {run.building}</span>}
                        {run?.checking > 0 && <span className="cd-chip b">✓ {run.checking}</span>}
                        {paused && <span className="cd-chip w">paused</span>}
                      </div>
                      {p.agent_name && <div className="cd-sub2">{p.agent_name}</div>}</td>
                    <td>{p.client?.name || p.client?.email || '—'}{p.client?.internal && <span className="cd-flag">us</span>}</td>
                    <td className="cd-sub2">{p.client?.provider_name || '—'}</td>
                    <td>{p.expert?.name || <span className="cd-sub2">unassigned</span>}</td>
                    <td><span className={`cd-chip ${p.phase === 'COMPLETED' ? 'g' : p.phase === 'FAILED' ? 'r' : p.phase ? 'b' : 'o'}`}>{(p.phase || p.project_status || '—').toLowerCase()}</span></td>
                    <td className="r m">{p.tickets?.completed ?? 0}/{p.tickets?.total ?? 0}
                      <div className="cd-sub2">{p.prs?.merged ?? 0} PRs · +{(p.loc?.additions || 0).toLocaleString()}</div></td>
                    <td className="r m">{usd2(p.cost?.total_usd)}<div className="cd-sub2">{usd2(p.cost?.total_30d_usd)} 30d</div></td>
                    <td className="r m cd-sub2">{ago(p.last_activity_at)}</td>
                    <td><span className={`cd-chip ${meta.cls}`}>{meta.label}</span></td>
                    <td className="r" onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        {paused
                          ? <button className="cd-btn" disabled={acting} style={act} title="Resume — automation runs again" onClick={() => doControl(p.id, 'resume')}><Play size={13} /></button>
                          : <button className="cd-btn" disabled={acting} style={act} title="Pause — stop builds and block every dispatch" onClick={() => doControl(p.id, 'pause')}><Pause size={13} /></button>}
                        <button className="cd-btn" disabled={acting || !(run?.building > 0 || run?.checking > 0)}
                          style={act} title="Stop the running builds (without pausing)" onClick={() => doControl(p.id, 'stop_builds')}><Square size={13} /></button>
                        <button className="cd-btn" disabled={acting} style={{ ...act, color: '#b42318', borderColor: '#f3c0ba' }} title="Delete the project, both sides" onClick={() => askDelete(p.id)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td className="cd-sub2" colSpan={10}>No projects match.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

    </>
  );
};

/** One tiny segmented D/W/M control — every chart carries its own. */
const BucketToggle = ({ value, onPick }) => (
  <span style={{ display: 'inline-flex', gap: 2 }}>
    {[['day', 'D'], ['week', 'W'], ['month', 'M']].map(([b, label]) => (
      <button key={b} className="cd-fchip" data-on={value === b} style={{ padding: '2px 8px', fontSize: 10 }} onClick={() => onPick(b)}>{label}</button>
    ))}
  </span>
);

const bucketLabels = (series) => {
  const ws = series?.weeks || [];
  return series?.bucket === 'month' ? ws.map((w) => w.slice(0, 7)) : ws.map((w) => w.slice(5).replace('-', '/'));
};

/** A chart panel with its OWN day/week/month filter. Series per bucket are
 *  fetched once and shared across all panels via ensureBucket. */
const SeriesPanel = ({ title, cap, byBucket, ensureBucket, render }) => {
  const [bucket, setBucket] = useState('week');
  const series = byBucket[bucket];
  useEffect(() => { ensureBucket(bucket); }, [bucket]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Panel title={title} tag="live" cap={cap} right={<BucketToggle value={bucket} onPick={setBucket} />}>
      <div className="cd-chart-body">
        {series ? render(series, bucketLabels(series)) : <div className="cd-sub2" style={{ padding: 20 }}>Loading…</div>}
      </div>
    </Panel>
  );
};

/**
 * The machine overview deck (CTO 10-12): the same verdict states as the
 * Projects lens, who is spending money right now, and every delivery chart —
 * each with its own daily/weekly/monthly filter. One projects fetch per
 * granularity, shared across all panels.
 */
const ProjectsNow = ({ theme }) => {
  // const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [byBucket, setByBucket] = useState({});
  const fetching = React.useRef(new Set());
  const ensureBucket = (b) => {
    if (byBucket[b] || fetching.current.has(b)) return;
    fetching.current.add(b);
    getDeckMachineSurface('projects', { bucket: b })
      .then((d) => setByBucket((m) => ({ ...m, [b]: d.series || null })))
      .catch(() => fetching.current.delete(b));
  };
  useEffect(() => {
    getDeckMachineSurface('projects')
      .then((d) => { setSt({ data: d }); setByBucket((m) => ({ ...m, week: d.series || null })); fetching.current.add('week'); })
      .catch((e) => setSt({ error: e?.response?.data?.detail || 'request failed' }));
  }, []);
  const t = CHART_THEME[theme] || CHART_THEME.light;
  if (st?.error || st?.data?.available === false) return null;
  const projects = st?.data?.projects || [];
  // const live = projects.filter((p) => (p.running?.building || 0) + (p.running?.checking || 0) > 0);
  const counts = {
    building: projects.filter((p) => p.running?.building > 0).length,
    checking: projects.filter((p) => p.running?.checking > 0).length,
    paused: projects.filter((p) => p.paused).length,
    stuck: st?.data?.totals?.stuck ?? 0,
    review: projects.filter((p) => ['review', 'ready'].includes(p.stuck?.state)).length,
    shipped: st?.data?.totals?.shipped ?? 0,
  };
  // Health: how many projects sit in each verdict state right now.
  const healthOrder = ['moving', 'checking', 'review', 'ready', 'needs_customer', 'needs_expert', 'erroring', 'stalled', 'unassigned', 'shipped'];
  const health = healthOrder
    .map((k) => ({ k, label: (STUCK_META[k] || {}).label || k, cls: (STUCK_META[k] || {}).cls || 'b', n: projects.filter((p) => p.stuck?.state === k).length }))
    .filter((h) => h.n > 0);
  const allHealthy = counts.stuck === 0;
  return (
    <>
    {/* Projects right now is hidden for now.
    <Panel title="Projects right now" tag="live"
      right={<button className="cd-link" onClick={() => navigate('/super/machine/projects')}>All projects <ArrowUpRight size={13} /></button>}
      cap="The same verdict states as the Projects lens — and who is spending money at this moment.">
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: live.length ? 10 : 0 }}>
        <span className="cd-chip g">● building · {counts.building}</span>
        <span className="cd-chip b">checking · {counts.checking}</span>
        <span className="cd-chip w">paused · {counts.paused}</span>
        <span className="cd-chip r">stuck · {counts.stuck}</span>
        <span className="cd-chip p">in review · {counts.review}</span>
        <span className="cd-chip o">shipped · {counts.shipped}</span>
      </div>
      {st && !st.data && <div className="cd-sub2">Loading…</div>}
      {live.length > 0 ? (
        <div style={{ maxHeight: 186, overflowY: 'auto', borderTop: '1px solid var(--cd-line, #eceae4)' }}>
          <table className="cd-t"><tbody>
            {live.map((p) => (
              <tr key={p.id} className="cl" onClick={() => navigate(`/super/machine/projects/${p.id}`)}>
                <td style={{ padding: '6px 10px' }}>
                  <span style={{ display: 'inline-block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{p.title || 'Untitled project'}</span>
                  <span className="cd-sub2" style={{ marginLeft: 8 }}>{p.client?.name || p.client?.email || ''}</span>
                </td>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                  {p.running?.building > 0 && <span className="cd-chip g">● {p.running.building}</span>}{' '}
                  {p.running?.checking > 0 && <span className="cd-chip b">✓ {p.running.checking}</span>}{' '}
                  {p.paused && <span className="cd-chip w">paused</span>}
                </td>
                <td className="r m" style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{usd2(p.cost?.total_30d_usd)} <span className="cd-sub2">30d</span></td>
              </tr>
            ))}
          </tbody></table>
        </div>
      ) : (st?.data ? <div className="cd-sub2">Nothing is building or checking right now.</div> : null)}
    </Panel>
    */}
    <div className="cd-charts">
      <SeriesPanel title="PRs merged" cap="PullRequest MERGED by mergedAt" byBucket={byBucket} ensureBucket={ensureBucket}
        render={(s, labels) => <Bar data={{ labels, datasets: [{ data: s.prs_merged || [], backgroundColor: t.s1, borderRadius: 3, maxBarThickness: 16 }] }} options={chartOpts(t)} />} />
      <SeriesPanel title="Tickets shipped" cap="tickets COMPLETED by completedAt" byBucket={byBucket} ensureBucket={ensureBucket}
        render={(s, labels) => <Bar data={{ labels, datasets: [{ data: s.tickets_shipped || [], backgroundColor: t.s1, borderRadius: 3, maxBarThickness: 16 }] }} options={chartOpts(t)} />} />
      <SeriesPanel title="Code written" cap="measured diffstat (deliveryEvidence.linesAdded) of delivered tickets — only measured work counts" byBucket={byBucket} ensureBucket={ensureBucket}
        render={(s, labels) => <Bar data={{ labels, datasets: [{ data: s.loc_added || [], backgroundColor: t.s2, borderRadius: 3, maxBarThickness: 16 }] }} options={chartOpts(t)} />} />
      <SeriesPanel title="Spend" cap="trusted LLM + compute, by ledger createdAt" byBucket={byBucket} ensureBucket={ensureBucket}
        render={(s, labels) => <Bar data={{ labels, datasets: [
          { label: 'LLM', data: s.llm_cost_usd || [], backgroundColor: t.s1, borderRadius: 3, maxBarThickness: 16 },
          { label: 'Compute', data: s.compute_cost_usd || [], backgroundColor: t.s2, borderRadius: 3, maxBarThickness: 16 },
        ] }} options={chartOpts(t, { legend: true, stacked: true, money: true })} />} />
      <SeriesPanel title="Created vs shipped" cap="projects born vs pipelines completed" byBucket={byBucket} ensureBucket={ensureBucket}
        render={(s, labels) => <Line data={{ labels, datasets: [
          { label: 'Created', data: s.projects_created || [], borderColor: t.s1, backgroundColor: t.s1, tension: 0.3, pointRadius: 2, borderWidth: 2 },
          { label: 'Shipped', data: s.agents_shipped || [], borderColor: t.s2, backgroundColor: t.s2, tension: 0.3, pointRadius: 2, borderWidth: 2 },
        ] }} options={chartOpts(t, { legend: true })} />} />
      <Panel title="Project health" tag="live" cap="every project's verdict state, right now — rules on each row of the Projects lens.">
        {allHealthy && <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}><span className="cd-chip g">all healthy</span><span className="cd-sub2">nothing needs a human, nothing is erroring or stalled</span></div>}
        <div style={{ display: 'grid', gap: 6, paddingTop: 6 }}>
          {health.map((h) => (
            <div key={h.k} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 28px', alignItems: 'center', gap: 8 }}>
              <span className="cd-sub2" style={{ textTransform: 'capitalize' }}>{h.label}</span>
              <div style={{ height: 8, borderRadius: 4, background: 'var(--cd-line, #eceae4)', overflow: 'hidden' }}>
                <div className={`cd-hbar ${h.cls}`} style={{ width: `${Math.max(4, Math.round((h.n / Math.max(1, projects.length)) * 100))}%`, height: '100%', borderRadius: 4 }} />
              </div>
              <span className="m" style={{ fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{h.n}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
    </>
  );
};

/*
 * Shared blockers companies have routed to the platform. An AI Champion found a
 * constraint their teams share that only platform operations can remove, and
 * sent it here. Acknowledge, resolve, or return it; the champion is notified.
 */
const PlatformBlockers = () => {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(null);
  const load = async () => {
    try { setEvents((await aaiApi.listPlatformBlockers()).data.events || []); }
    catch { setEvents([]); }
  };
  useEffect(() => { load(); }, []);
  const live = events.filter((e) => !isClosed(e));
  return (
    <>
      <Panel title="Routed to platform" tag={live.length ? 'live' : 'gap'} right={<span className="cd-sub2">{live.length} open</span>}>
        {live.length === 0 && <div className="cd-sub2" style={{ padding: '12px 15px' }}>Nothing routed to platform operations. A champion sends a shared blocker here; an Owner sends a capability evaluation request.</div>}
        {live.map((e, i) => {
          const state = statusOf(e.status);
          return (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 15px', borderTop: i === 0 ? 0 : '1px solid var(--cd-line, #E7E7E4)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subject}</div>
                <div className="cd-sub2" style={{ marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.company_name} · {e.kind === 'evaluation_request' ? 'evaluation request' : 'shared blocker'} · raised by {e.actor_email} · {state.label} · response {String((e.payload || {}).response_by || 'not set')}</div>
              </div>
              <button type="button" className="cd-link" style={{ flex: 'none' }} onClick={() => setOpen(e)}>Respond</button>
            </div>
          );
        })}
      </Panel>
      {open && <ActionUpdateDrawer event={open} onUpdated={() => { setOpen(null); load(); }} onClose={() => setOpen(null)} />}
    </>
  );
};

// AA Compass — the real routing decisions, read person-first. Two groupings
// over the same enriched jev-judgments feed (decision.* parsed from
// stateSnapshot server-side; client names stamped on by the aibot account
// plane; spend.by_ticket / spend.by_run rolled up from AiRuntimeUsage on the
// builder): planning sessions (which ticket each one talked to, when it was
// born, what it ate) and tickets (whole-ticket spend + models that did the
// work). Every row expands into the step-by-step decisions underneath.
// Advice calls and synthetic probes stay summarised below, never mixed in.
const shortModel = (m) => !m ? '—' : String(m).replace('accounts/fireworks/models/', '').replace('accounts-fireworks-models-', '').replace('accounts/fireworks/routers/', '');
const COMPASS_SURFACES = [['all', 'All'], ['planner', 'Planner'], ['execution', 'Execution'], ['qa', 'QA'], ['runtime', 'Workspace'], ['developer', 'Developer']];
// Machine spend is routinely sub-cent — two decimals would round real money
// to $0.00, so small figures keep four.
const eatUsd = (n) => n == null ? '—' : `$${Number(n) >= 1 ? Number(n).toFixed(2) : Number(n).toFixed(4)}`;
const bornAt = (iso) => iso ? <span title={new Date(iso).toLocaleString()}>{ago(iso)} ago</span> : '—';

// What a session/ticket ate: headline = machine usage value (tokens × catalog
// rate, trusted rows); sub-line = what AA itself paid, + unrated remainder.
const SpendCell = ({ sp }) => {
  if (!sp) return <span className="cd-sub2" title="Spend rollup not available for this row yet">—</span>;
  return (
    <>
      <span className="m">{eatUsd(sp.usage_usd)}</span>
      <div className="cd-sub2">AA paid {eatUsd(sp.incurred_usd)}{sp.untrusted_usd > 0 ? ` · +${eatUsd(sp.untrusted_usd)} unrated` : ''}</div>
    </>
  );
};

// The models that actually burned money (from the spend rollup), with the
// judgment-feed "what ran" models as the fallback when no rollup exists.
const ModelsCell = ({ sp, ran }) => {
  const names = sp?.models?.length ? sp.models.map((m) => shortModel(m.model)) : ran;
  if (!names?.length) return <span className="cd-sub2">—</span>;
  const shown = names.slice(0, 3);
  return (
    <span className="cd-sub2 m" title={names.join(', ')}>
      {shown.join(', ')}{names.length > shown.length ? ` +${names.length - shown.length}` : ''}
    </span>
  );
};

// One decision per step — the expanded body of a session or ticket row.
const DecisionSteps = ({ js }) => (
  <div style={{ overflowX: 'auto', background: 'var(--cd-blue-soft)', padding: '4px 0 8px' }}>
    <table className="cd-t">
      <thead><tr><th>When</th><th>Step</th><th>Compass pick</th><th className="r">Confidence</th><th>Runner-up</th><th>What ran</th><th>Outcome</th></tr></thead>
      <tbody>
        {js.map((j) => {
          const dec = j.decision || {};
          const runner = (dec.top || []).find((t) => t.model !== dec.choice);
          const agree = dec.selected_model && dec.choice && shortModel(dec.selected_model) === shortModel(dec.choice);
          return (
            <tr key={j.id}>
              <td className="cd-sub2 m">{bornAt(j.created_at)}</td>
              <td><span className="cd-chip o">{j.step?.kind || j.lane}</span></td>
              <td className="m">{shortModel(dec.choice)}</td>
              <td className="r m">{dec.confidence == null ? '—' : Number(dec.confidence).toFixed(2)}</td>
              <td className="cd-sub2 m">{runner ? `${shortModel(runner.model)} · ${Number(runner.p).toFixed(2)}` : '—'}</td>
              <td className="m">{shortModel(dec.selected_model)}{agree ? ' ✓' : ''}</td>
              <td>{dec.error ? <span className="cd-chip w">fell back</span> : <span className="cd-chip g">applied</span>}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const QaChip = ({ qa }) => {
  if (!qa) return null;
  if (qa.status === 'failed') return <span className="cd-chip o">QA run died</span>;
  if (qa.status !== 'completed') return <span className="cd-chip o">QA {qa.status}</span>;
  return (qa.failed || 0) > 0
    ? <span className="cd-chip r">QA · {qa.failed} failed</span>
    : <span className="cd-chip g">QA passed</span>;
};

const CompassDecisions = () => {
  const [st, setSt] = useState({ loading: true });
  const [f, setF] = useState({ surface: 'all', client: 'all', project: 'all', ticket: 'all', q: '' });
  const [open, setOpen] = useState({});
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  useEffect(() => {
    getDeckMachineSurface('jev-judgments', { limit: '500', kind: 'routing' })
      .then((d) => setSt({ data: d }))
      .catch((e) => setSt({ error: e?.response?.data?.detail || 'request failed' }));
  }, []);
  if (st.loading) return <Panel title="AA Compass decisions" tag="live"><div className="cd-sub2" style={{ padding: '10px 15px' }}>Loading decisions…</div></Panel>;
  if (st.error) return <Panel title="AA Compass decisions" tag="gap"><div className="cd-sub2" style={{ padding: '10px 15px' }}>{st.error}</div></Panel>;
  if (st.data?.available === false) return <Panel title="AA Compass decisions" tag="gap"><div className="cd-sub2" style={{ padding: '10px 15px' }}>Builder unreachable ({st.data.reason || 'unknown'}). Decisions appear when the connection recovers.</div></Panel>;
  const judgments = st.data?.judgments || [];
  const spend = st.data?.spend || null;
  const routing = judgments.filter((j) => j.decision?.kind === 'routing');

  // Human names everywhere — ids stay option VALUES, never labels.
  const cidOf = (j) => j.project?.external_client_id || j.project?.workspace_id || null;
  const clientName = (j) => j.project?.client?.name || j.project?.client?.email || null;
  const projectName = (p) => p?.title || p?.agent_name || 'Untitled project';
  const whoLine = (j) => [clientName(j) || 'Unnamed client', j.project ? projectName(j.project) : null].filter(Boolean).join(' · ');

  const clientOpts = new Map();
  routing.forEach((j) => { const id = cidOf(j); if (id && !clientOpts.has(id)) clientOpts.set(id, clientName(j) || 'Unnamed client'); });
  const projMap = new Map();
  routing.forEach((j) => { if (j.project?.id && (f.client === 'all' || cidOf(j) === f.client)) projMap.set(j.project.id, j.project); });
  const projects = [...projMap.values()];
  const tktMap = new Map();
  routing.forEach((j) => { if (j.ticket?.id && (f.project === 'all' || j.project?.id === f.project) && (f.client === 'all' || cidOf(j) === f.client)) tktMap.set(j.ticket.id, j.ticket); });
  const tickets = [...tktMap.values()];

  const rows = routing.filter((j) => {
    if (f.surface !== 'all' && j.lane !== f.surface) return false;
    if (f.client !== 'all' && cidOf(j) !== f.client) return false;
    if (f.project !== 'all' && j.project?.id !== f.project) return false;
    if (f.ticket !== 'all' && j.ticket?.id !== f.ticket) return false;
    if (f.q) {
      const hay = `${j.ticket?.title || ''} ${clientName(j) || ''} ${j.project?.title || ''} ${j.decision?.choice || ''} ${j.decision?.selected_model || ''} ${j.decision?.error || ''}`.toLowerCase();
      if (!hay.includes(f.q.toLowerCase())) return false;
    }
    return true;
  });

  const byTimeAsc = (a, b) => new Date(a.created_at) - new Date(b.created_at);
  const spendFor = (runId, ticketId) => (runId && spend?.by_run?.[runId]) || (ticketId && spend?.by_ticket?.[ticketId]) || null;
  const ranModels = (js) => [...new Set(js.map((j) => shortModel(j.decision?.selected_model || j.candidate_model)).filter((m) => m && m !== '—'))];

  // Planning sessions — planner-lane decisions grouped by engine run (the
  // session), falling back to the conversation/ticket key when no run id.
  const sessMap = new Map();
  rows.filter((j) => j.lane === 'planner').forEach((j) => {
    const key = j.run_id || j.ticket?.id || j.id;
    if (!sessMap.has(key)) sessMap.set(key, []);
    sessMap.get(key).push(j);
  });
  const sessions = [...sessMap.entries()].map(([key, js]) => {
    const sorted = js.slice().sort(byTimeAsc);
    const first = sorted[0];
    const withTicket = sorted.find((j) => j.ticket?.title);
    return {
      key, js: sorted, born: first.created_at, last: sorted[sorted.length - 1].created_at,
      ticket: withTicket?.ticket || null, who: whoLine(first),
      sp: spendFor(first.run_id, first.ticket?.id), ran: ranModels(sorted),
    };
  }).sort((a, b) => new Date(b.last) - new Date(a.last));

  // Tickets — every decision that resolved to a real ticket, whole-ticket view.
  const tgMap = new Map();
  rows.filter((j) => j.ticket?.id).forEach((j) => {
    if (!tgMap.has(j.ticket.id)) tgMap.set(j.ticket.id, []);
    tgMap.get(j.ticket.id).push(j);
  });
  const ticketGroups = [...tgMap.entries()].map(([key, js]) => {
    const sorted = js.slice().sort(byTimeAsc);
    const t = sorted.find((j) => j.ticket?.title)?.ticket || sorted[0].ticket;
    return {
      key, js: sorted, t, who: whoLine(sorted[0]),
      born: t?.created_at || sorted[0].created_at, last: sorted[sorted.length - 1].created_at,
      sp: spend?.by_ticket?.[key] || null, ran: ranModels(sorted),
    };
  }).sort((a, b) => new Date(b.last) - new Date(a.last));

  return (
    <>
      <Panel title={`AA Compass decisions · ${rows.length}`} tag="live" cap="Real routing decisions — the Choice router picking a model per step. Grouped below into planning sessions and tickets; click a row to open every decision inside it.">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '11px 15px' }}>
          {COMPASS_SURFACES.map(([v, l]) => <button key={v} className="cd-fchip" data-on={f.surface === v} onClick={() => setF((s) => ({ ...s, surface: v }))}>{l}</button>)}
          <select className="cd-sel" value={f.client} onChange={(e) => setF((s) => ({ ...s, client: e.target.value, project: 'all', ticket: 'all' }))}>
            <option value="all">All clients</option>
            {[...clientOpts.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select className="cd-sel" value={f.project} onChange={(e) => setF((s) => ({ ...s, project: e.target.value, ticket: 'all' }))}>
            <option value="all">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{projectName(p)}</option>)}
          </select>
          <select className="cd-sel" value={f.ticket} onChange={(e) => setF((s) => ({ ...s, ticket: e.target.value }))} disabled={tickets.length === 0}>
            <option value="all">All tickets</option>
            {tickets.map((t) => <option key={t.id} value={t.id}>{(t.title || 'Untitled ticket').slice(0, 48)}</option>)}
          </select>
          <input className="cd-finput" placeholder="Search ticket, client, model, outcome…" value={f.q} onChange={(e) => setF((s) => ({ ...s, q: e.target.value }))} />
        </div>
      </Panel>

      <Panel title={`Planning sessions · ${sessions.length}`} tag="live" cap="Planner-lane sessions — which ticket each one talked to, when it was born, and what it ate. 'Ate' is the machine usage value (tokens × catalog rate); 'AA paid' is our incurred cost.">
        <div style={{ overflowX: 'auto' }}>
          <table className="cd-t">
            <thead><tr><th style={{ width: 24 }} /><th>Session</th><th>Talks to</th><th>Born</th><th className="r">Decisions</th><th>Models</th><th className="r">Ate</th></tr></thead>
            <tbody>
              {sessions.map((s) => {
                const k = `s:${s.key}`;
                return (
                  <React.Fragment key={k}>
                    <tr className="cl" onClick={() => toggle(k)} style={{ cursor: 'pointer' }}>
                      <td className="cd-sub2">{open[k] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td>Planning session<div className="cd-sub2">{s.who}</div></td>
                      <td>{s.ticket ? s.ticket.title : <span className="cd-sub2" style={{ fontStyle: 'italic' }}>planner conversation — no ticket yet</span>}</td>
                      <td className="cd-sub2 m">{bornAt(s.born)}</td>
                      <td className="r m">{s.js.length}</td>
                      <td><ModelsCell sp={s.sp} ran={s.ran} /></td>
                      <td className="r"><SpendCell sp={s.sp} /></td>
                    </tr>
                    {open[k] && <tr><td colSpan={7} style={{ padding: 0 }}><DecisionSteps js={s.js} /></td></tr>}
                  </React.Fragment>
                );
              })}
              {sessions.length === 0 && <tr><td className="cd-sub2" colSpan={7}>No planner sessions match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={`Tickets · ${ticketGroups.length}`} tag="live" cap="The whole ticket: everything it cost, the models that did the work, and — expanded — each routing decision per step. ✓ = the pick is what actually ran.">
        <div style={{ overflowX: 'auto' }}>
          <table className="cd-t">
            <thead><tr><th style={{ width: 24 }} /><th>Ticket</th><th>State</th><th>Born</th><th className="r">Decisions</th><th>Models used</th><th className="r">Whole cost</th></tr></thead>
            <tbody>
              {ticketGroups.map((g) => {
                const k = `t:${g.key}`;
                return (
                  <React.Fragment key={k}>
                    <tr className="cl" onClick={() => toggle(k)} style={{ cursor: 'pointer' }}>
                      <td className="cd-sub2">{open[k] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td>{g.t?.title || 'Untitled ticket'}<div className="cd-sub2">{g.who}</div></td>
                      <td>{g.t?.status ? <span className="cd-chip b">{String(g.t.status).toLowerCase()}</span> : <span className="cd-sub2">—</span>} <QaChip qa={g.t?.qa_outcome} /></td>
                      <td className="cd-sub2 m">{bornAt(g.born)}</td>
                      <td className="r m">{g.js.length}</td>
                      <td><ModelsCell sp={g.sp} ran={g.ran} /></td>
                      <td className="r"><SpendCell sp={g.sp} /></td>
                    </tr>
                    {open[k] && <tr><td colSpan={7} style={{ padding: 0 }}><DecisionSteps js={g.js} /></td></tr>}
                  </React.Fragment>
                );
              })}
              {ticketGroups.length === 0 && <tr><td className="cd-sub2" colSpan={7}>No ticket-bound decisions match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="cd-foot">
          Only genuine model-routing decisions (<span className="cd-mono">live-swarm-session</span>) are listed. In-loop advice and synthetic probes are summarised in the judgment distribution below.
          {!spend && <> Ticket and session spend appears once the builder ships the <span className="cd-mono">spend.by_ticket / by_run</span> rollup on jev-shadow-judgments.</>}
        </div>
      </Panel>
    </>
  );
};

// Experts — assign an expert to a project (and auto-create if new). Uses the
// already-live provisioning path (assign-expert / fde-status), audited to the
// acting super. Approve/reject/model-allowlist/team-login are tRPC-only on the
// builder today and get their provisioning twins in a later wave.
const SERVICE_MODELS = [['FDE', 'FDE — embedded engineer'], ['BLOCKER_SUPPORT', 'Blocker support — bounded help']];
const ExpertAssign = ({ experts }) => {
  const [projects, setProjects] = useState(null);
  const [form, setForm] = useState({ email: '', projectId: '', serviceModel: 'FDE' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    getDeckMachineSurface('projects')
      .then((d) => setProjects(d.projects || []))
      .catch(() => setProjects([]));
  }, []);
  const assign = async () => {
    if (!form.email || !form.projectId) { toast.error('Pick an expert and a project'); return; }
    setBusy(true);
    try {
      await sendDeckMachineWrite('assign-expert', { project_id: form.projectId, email: form.email, service_model: form.serviceModel }, 'post');
      toast.success('Expert assigned — live on the project, audited to you');
      setForm((s) => ({ ...s, projectId: '' }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Assign an expert to a project" tag="live"
      cap="Writes live via the provisioning path, audited to you. Auto-creates the expert if the email is new.">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', padding: '12px 15px' }}>
        <select className="cd-sel" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}>
          <option value="">Expert…</option>
          {(experts || []).filter((x) => x.email).map((x) => <option key={x.id} value={x.email}>{x.name || x.email}</option>)}
        </select>
        <select className="cd-sel" value={form.projectId} onChange={(e) => setForm((s) => ({ ...s, projectId: e.target.value }))}>
          <option value="">{projects == null ? 'Loading projects…' : 'Project…'}</option>
          {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.title || p.agent_name || 'Untitled project'}</option>)}
        </select>
        <select className="cd-sel" value={form.serviceModel} onChange={(e) => setForm((s) => ({ ...s, serviceModel: e.target.value }))}>
          {SERVICE_MODELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button className="cd-btn pri" disabled={busy} onClick={assign}>{busy ? 'Assigning…' : 'Assign'}</button>
      </div>
    </Panel>
  );
};

/** General — Slack notification routing (builder admin parity, read + cut). */
const GeneralTab = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => getDeckMachineSurface('notifications').then((d) => setSt({ data: d })).catch((e) => setSt({ error: e?.response?.data?.detail || 'request failed' }));
  useEffect(() => { load(); }, []);
  const disconnect = async (type) => {
    setBusy(type);
    try { await deleteDeckMachine('notifications', { type }); toast.success('Routing disconnected'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'disconnect failed'); }
    finally { setBusy(''); }
  };
  const channels = st?.data?.channels || [];
  return (
    <Panel title="Slack notification routing" tag="live"
      cap="Where each platform event lands in Slack. Disconnect works from here; wiring a NEW channel runs Slack's own install flow (the link lives on the builder).">
      {st?.error && <div className="cd-sub2">{st.error}</div>}
      <table className="cd-t"><tbody>
        {channels.map((c) => (
          <tr key={c.type}>
            <td>{c.label}<div className="cd-sub2 m">{c.type}</div></td>
            <td>{c.connected ? <span className="cd-chip g">#{c.channelName}{c.teamName ? ` · ${c.teamName}` : ''}</span> : <span className="cd-chip o">not routed</span>}</td>
            <td className="r">{c.connected && <button className="cd-btn sm" disabled={busy === c.type} onClick={() => disconnect(c.type)}>Disconnect</button>}</td>
          </tr>
        ))}
        {channels.length === 0 && <tr><td className="cd-sub2" colSpan={3}>{st ? 'No notification types.' : 'Loading…'}</td></tr>}
      </tbody></table>
    </Panel>
  );
};

const CommandDeck = () => {
  const navigate = useNavigate();
  // Mode + page come from the URL now — the super shell owns the Business/Machine
  // header switch and the mode-driven sidebar; this component just renders the
  // page for /super/:deckMode/:deckTab. Deep-linkable, ⌘K-jumpable.
  const location = useLocation();
  const { deckTab, projectId, companyId } = useParams();
  const mode = location.pathname.includes('/super/machine') ? 'machine' : 'business';
  let tab = deckTab || 'overview';
  if (companyId) tab = 'usage';
  // if (billId) tab = 'hosting';
  if (projectId) tab = 'projects';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  // Reconciled nightly "true total" for the open account — persisted,
  // stripe_customer_id-keyed join of this side's token_usage against
  // builder's AiRuntimeUsage/TicketRun ledgers (usage_reconciliation table).
  // Distinct from the live cogs_usd above it: this is what the nightly job
  // actually wrote, so it survives a builder outage during the run and
  // carries its own reconciled_at trail.
  const [recon, setRecon] = useState(null);
  useEffect(() => {
    if (!drawer?.id) { setRecon(null); return; }
    setRecon({ loading: true });
    getDeckMachineSurface('usage-reconciliation', { client_id: drawer.id, days: '30' })
      .then((d) => setRecon({ data: d }))
      .catch((e) => setRecon({ error: e?.response?.data?.detail || 'request failed' }));
  }, [drawer?.id]);
  const [theme, setTheme] = useState(() => localStorage.getItem('aa_theme') || 'light');

  useEffect(() => {
    const sync = () => setTheme(localStorage.getItem('aa_theme') || 'light');
    window.addEventListener('aa-theme', sync);
    window.addEventListener('storage', sync);
    return () => { window.removeEventListener('aa-theme', sync); window.removeEventListener('storage', sync); };
  }, []);

  const load = async () => {
    try { setData(await getCommandDeck()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load the deck'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  // Machine admin surfaces (CD3 read proxy) — fetched lazily, once per tab.
  // Payloads carry available:false (with a reason) when the builder is down;
  // the blocks render that honestly instead of blanks.
  const [surf, setSurf] = useState({});
  useEffect(() => {
    const path = MACHINE_SURFACES[tab];
    if (mode !== 'machine' || !path) return;
    if (surf[tab]?.data || surf[tab]?.loading) return;
    setSurf((p) => ({ ...p, [tab]: { loading: true } }));
    getDeckMachineSurface(path)
      .then((d) => setSurf((p) => ({ ...p, [tab]: { data: d } })))
      .catch((e) => setSurf((p) => ({ ...p, [tab]: { error: e?.response?.data?.detail || 'request failed' } })));
  }, [mode, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-pull one surface after a write (the lazy effect only fires on tab change).
  const reloadSurface = (tabKey) => {
    const path = MACHINE_SURFACES[tabKey];
    if (!path) return;
    setSurf((p) => ({ ...p, [tabKey]: { loading: true } }));
    getDeckMachineSurface(path)
      .then((d) => setSurf((p) => ({ ...p, [tabKey]: { data: d } })))
      .catch((e) => setSurf((p) => ({ ...p, [tabKey]: { error: e?.response?.data?.detail || 'request failed' } })));
  };

  const writeControls = async (body) => {
    try {
      await sendDeckMachineWrite('controls', body, 'post');
      toast.success('Controls updated');
      reloadSurface('cost');
      reloadSurface('margin');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Controls update failed'); }
  };

  // Capabilities — CD3 writes (create/adopt/always-on/delete), the same
  // action-dispatch body shape the builder's own admin console posts.
  const saveCapability = async (body) => {
    try {
      const r = await sendDeckMachineWrite('caps', body, 'post');
      reloadSurface('caps');
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Capability action failed');
      throw e;
    }
  };
  const importCapability = async (body) => {
    try {
      return await sendDeckCapabilitiesImport(body);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
      throw e;
    }
  };
  const [capCreate, setCapCreate] = useState(null); // null | 'skill' | 'plugin'
  const [capCreateForm, setCapCreateForm] = useState({ name: '', description: '', instructions: '', category: '' });
  const [capCreateBusy, setCapCreateBusy] = useState(false);
  const [capImportOpen, setCapImportOpen] = useState(false);
  const [capImportState, setCapImportState] = useState({ mode: 'github', source: '', file: null, busy: false, items: null, selected: {} });

  const loadMoreAudit = () => {
    const cur = surf.audit?.data;
    if (!cur?.next_before || surf.audit?.more) return;
    setSurf((p) => ({ ...p, audit: { ...p.audit, more: true } }));
    getDeckMachineSurface('audit', { before: cur.next_before })
      .then((d) => setSurf((p) => ({
        ...p,
        audit: { data: { ...cur, events: [...(cur.events || []), ...(d.events || [])], next_before: d.next_before }, more: false },
      })))
      .catch(() => setSurf((p) => ({ ...p, audit: { ...p.audit, more: false } })));
  };

  const openBuilder = async (clientId) => {
    try {
      const { url } = await requestAgentBridgeUrl(clientId ? undefined : '/admin/settings', clientId);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch { toast.error('Could not open the builder'); }
  };

  const rev = data?.revenue || {};
  const bm = data?.builder_metrics || {};
  const perClient = data?.per_client || [];

  // Portfolio economics from the per-account rows (keeps revenue and cost on
  // the same basis). Contribution = revenue - COGS; margin = contribution/rev.
  const econ = useMemo(() => {
    const revenue = perClient.reduce((s, c) => s + (c.revenue_usd || 0), 0);
    const cogs = perClient.reduce((s, c) => s + (c.cogs_usd || 0), 0);
    const contribution = revenue - cogs;
    return { revenue, cogs, contribution, marginPct: revenue ? (contribution / revenue) * 100 : null };
  }, [perClient]);

  const accountsByMargin = useMemo(
    () => [...perClient].sort((a, b) => (a.margin_usd ?? a.revenue_usd) - (b.margin_usd ?? b.revenue_usd)),
    [perClient]
  );
  const accountsByRev = useMemo(
    () => [...perClient].sort((a, b) => (b.revenue_usd || 0) - (a.revenue_usd || 0)),
    [perClient]
  );

  const arr = rev.mrr_usd != null ? rev.mrr_usd * 12 : null;

  if (mode === 'machine' && (tab === 'usage' || tab === 'ai-cost' || tab === 'incidents' || tab === 'live')) return (
    <div className="cd-root" data-cd-theme={theme}>
      <style>{DECK_CSS}</style>
      {tab === 'usage' && <CompanyUsageTab />}
      {tab === 'ai-cost' && <AiCostMarginTab />}
      {/* AWS hosting is hidden for now. {tab === 'hosting' && <HostingTab />} */}
      {tab === 'incidents' && <AgentIncidentsTab />}
      {tab === 'live' && <LiveTab />}
    </div>
  );

  if (loading) return (
    <div className="cd-root" data-cd-theme={theme}>
      <style>{DECK_CSS}</style>
      <div style={{ display: 'grid', placeItems: 'center', paddingTop: 120 }}><Loader2 className="animate-spin" style={{ color: 'var(--cd-mut2)' }} /></div>
    </div>
  );
  if (!data) return null;

  // ── alarms (real) ──
  const alarms = [];
  if (mode === 'business') {
    if (rev.outstanding_usd > 0) alarms.push(['red', `${usd(rev.outstanding_usd)} outstanding`]);
    if (data.loop?.unrouted > 0) alarms.push(['warn', `${data.loop.unrouted} unrouted request${data.loop.unrouted === 1 ? '' : 's'}`]);
  } else {
    if (!bm.available) alarms.push(['warn', `Builder metrics unavailable (${bm.reason || 'unreachable'})`]);
    if (bm.available && bm.totals?.untrusted_usd > 0) alarms.push(['warn', `${usd2(bm.totals.untrusted_usd)} untrusted spend — mislabeled open-lane phantom`]);
  }

  const marginColor = (p) => p == null ? 'var(--cd-mut2)' : p >= 80 ? 'var(--cd-good)' : p >= 50 ? 'var(--cd-warn)' : 'var(--cd-red)';

  return (
    <div className="cd-root" data-cd-theme={theme}>
      <style>{DECK_CSS}</style>

      {/* updated stamp (mode switch + page nav now live in the super shell) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        {mode === 'business' && tab === 'overview' && (
          <button className="cd-btn sm" onClick={() => openBuilder()}>Builder settings <ArrowUpRight size={13} /></button>
        )}
        <div style={{ fontSize: 11, color: 'var(--cd-mut2)' }} className="cd-mono">
          updated {data.generated_at_epoch ? new Date(data.generated_at_epoch * 1000).toLocaleTimeString() : 'now'} · 60s
        </div>
      </div>

      {/* alarms */}
      {alarms.length > 0 && (
        <div className="cd-alarms">
          {alarms.map(([kind, txt], i) => <div key={i} className={`cd-al ${kind}`}><span className="d" />{txt}</div>)}
        </div>
      )}

      {/* ═══════════════ BUSINESS ═══════════════ */}
      {mode === 'business' && tab === 'overview' && (
        <>
          <div className="cd-legend">
            <span><St k="live" /> from Postgres today</span>
            <span><St k="est" /> directional, needs a fix</span>
            <span><St k="gap" /> instrumentation required</span>
          </div>
          <div className="cd-kpis">
            <Kpi eyebrow="MRR" tag="live" value={usd(rev.mrr_usd)} vclass="accent"
              sub={`ARR ${usd(arr)} · ${rev.paying_customers || 0} paying`} fx="Σ active·non-free subs" />
            <Kpi eyebrow="Recognized rev · 30d" tag="live" value={usd(rev.recognized_30d_usd)}
              sub="incl. top-ups + deep-scoping" fx="Σ paid invoices by paid_at" />
            <Kpi eyebrow="Gross margin" tag="est" value={pct(econ.marginPct)} vclass="good"
              sub={`contribution ${usd(econ.contribution)}`} fx="(rev − true COGS) ÷ rev" />
            <Kpi eyebrow="Outstanding AR" tag="live" value={usd(rev.outstanding_usd)} vclass="red"
              sub={`${rev.invoices_30d || 0} paid invoices 30d`} fx="Σ open invoices.amount_remaining" />
          </div>
          <PlatformBlockers />
          <div className="cd-grid2">
            <Panel title="Margin by account" tag="live"
              right={<button className="cd-link" onClick={() => navigate('/super/business/accounts')}>All accounts <ArrowUpRight size={13} /></button>}
              cap="Click any account → full P&L and the pennies behind the cost.">
              <table className="cd-t">
                <thead><tr><th>Account</th><th className="r">Revenue</th><th className="r">True cost</th><th className="r">Margin</th><th style={{ width: 80 }} /></tr></thead>
                <tbody>
                  {accountsByMargin.slice(0, 6).map((c) => (
                    <tr key={c.id} className="cl" onClick={() => setDrawer(c)}>
                      <td>{c.name || c.email}<div className="cd-sub2">{c.has_consultant ? 'assigned' : 'unassigned'}</div></td>
                      <td className="r m">{usd(c.revenue_usd)}</td>
                      <td className="r m">{usd2(c.cogs_usd)}</td>
                      <td className="r m" style={{ color: marginColor(c.margin_pct) }}>{c.margin_pct == null ? '—' : pct(c.margin_pct)}</td>
                      <td className="r"><div className="cd-mbar"><i style={{ width: `${Math.max(2, Math.min(100, c.margin_pct || 0))}%`, background: marginColor(c.margin_pct) }} /></div></td>
                    </tr>
                  ))}
                  {perClient.length === 0 && <tr><td className="cd-sub2" colSpan={5}>No accounts in range.</td></tr>}
                </tbody>
              </table>
            </Panel>
            <div>
              <Panel title="Portfolio" tag="live">
                <table className="cd-t"><tbody>
                  <tr><td>Paying accounts</td><td className="r m">{rev.paying_customers || 0}</td></tr>
                  <tr><td>Total accounts</td><td className="r m">{data.clients?.total || 0}</td></tr>
                  <tr><td>New · 30d</td><td className="r m">{data.clients?.new_30d || 0}</td></tr>
                  <tr><td>With consultant</td><td className="r m">{data.clients?.with_consultant || 0}</td></tr>
                </tbody></table>
              </Panel>
              <Panel title="The loop" tag="live">
                <table className="cd-t"><tbody>
                  <tr><td>Open requests</td><td className="r m">{data.loop?.open_requests || 0}</td></tr>
                  <tr><td>Unrouted <span className="cd-sub2">no dept</span></td><td className="r m" style={{ color: data.loop?.unrouted ? 'var(--cd-warn)' : undefined }}>{data.loop?.unrouted || 0}</td></tr>
                  <tr><td>Live sessions</td><td className="r m">{data.sessions?.live || 0}</td></tr>
                </tbody></table>
              </Panel>
            </div>
          </div>
          <div className="cd-foot">aibot <span className="cd-mono">GET /super/command-deck</span> ⋈ builder <span className="cd-mono">/api/provisioning/platform-metrics</span></div>
        </>
      )}

      {mode === 'business' && tab === 'accounts' && (
        <Panel title="Accounts — unit economics" tag="live"
          cap="COGS = LLM (ai_runtime_usage ⋈ Project.workspaceId) + compute (compute_usage) + aibot chat (token_usage). Sorted worst-margin first.">
          <table className="cd-t">
            <thead><tr><th>Account</th><th className="r">Rev 30d</th><th className="r">COGS 30d</th><th className="r">Contribution</th><th className="r">Margin</th><th className="r" /></tr></thead>
            <tbody>
              {accountsByMargin.map((c) => (
                <tr key={c.id} className="cl" onClick={() => setDrawer(c)}>
                  <td>{c.name || c.email}<div className="cd-sub2">{c.email}</div></td>
                  <td className="r m">{usd(c.revenue_usd)}</td>
                  <td className="r m">{usd2(c.cogs_usd)}</td>
                  <td className="r m" style={{ color: (c.margin_usd || 0) < 0 ? 'var(--cd-red)' : undefined }}>{usd2(c.margin_usd)}</td>
                  <td className="r m" style={{ color: marginColor(c.margin_pct) }}>{c.margin_pct == null ? '—' : pct(c.margin_pct)}</td>
                  <td className="r"><button className="cd-link" onClick={(e) => { e.stopPropagation(); openBuilder(c.id); }}><ArrowUpRight size={13} /></button></td>
                </tr>
              ))}
              {perClient.length === 0 && <tr><td className="cd-sub2" colSpan={6}>No accounts.</td></tr>}
            </tbody>
          </table>
        </Panel>
      )}

      {mode === 'business' && tab === 'revenue' && (
        <>
          <div className="cd-note"><b>Revenue capture is whole.</b> Top-ups (agent build, session, seat) and $999 deep-scoping now write a local <span className="cd-mono">invoices</span> row on the Stripe event (idempotent, tagged in <span className="cd-mono">invoice_metadata</span>), so recognized revenue includes them. Still open: refunds — no <span className="cd-mono">charge.refunded</span> handler. <St k="gap" /></div>
          <div className="cd-kpis">
            <Kpi eyebrow="Collected · 30d" tag="live" value={usd(rev.recognized_30d_usd)} fx="Σ paid invoices" />
            <Kpi eyebrow="Outstanding" tag="live" value={usd(rev.outstanding_usd)} vclass="red" fx="open invoices remaining" />
            <Kpi eyebrow="MRR" tag="live" value={usd(rev.mrr_usd)} fx="Σ active non-free subs" />
            <Kpi eyebrow="Refunds · 30d" tag="live" value={usd(rev.refunds_30d_usd)} fx="charge.refunded → negative invoice; nets out of recognized" />
          </div>
          <Panel title="Revenue by account (paid, 30d)" tag="live">
            <table className="cd-t"><thead><tr><th>Account</th><th className="r">Revenue</th></tr></thead><tbody>
              {accountsByRev.filter((c) => c.revenue_usd > 0).slice(0, 12).map((c) => (
                <tr key={c.id}><td>{c.name || c.email}</td><td className="r m">{usd(c.revenue_usd)}</td></tr>
              ))}
              {accountsByRev.filter((c) => c.revenue_usd > 0).length === 0 && <tr><td className="cd-sub2" colSpan={2}>No paid revenue in range.</td></tr>}
            </tbody></table>
          </Panel>
          <Panel title="MRR movement & AR aging" tag="gap"
            cap="Aging buckets + MRR movement need per-invoice due-date bucketing and periodic MRR snapshots (no history table today).">
            <div style={{ padding: '14px 15px', fontSize: 12.5, color: 'var(--cd-muted)' }}>
              Add an <span className="cd-mono">mrr_snapshot</span> table + due-date bucketing to light up new / expansion / contraction / churn and 0–30 / 30–60 / 60–90 aging. Named on Data Health.
            </div>
          </Panel>
        </>
      )}

      {mode === 'business' && tab === 'growth' && (
        <>
          <div className="cd-kpis">
            <Kpi eyebrow="New accounts · 30d" tag="live" value={data.clients?.new_30d || 0} vclass="accent" fx="client.created_at, internal excluded" />
            <Kpi eyebrow="Open loop" tag="live" value={data.loop?.open_requests || 0} fx="human_request status=open" />
            <Kpi eyebrow="Net revenue retention" tag={data.nrr_30d != null ? 'live' : 'gap'}
              value={data.nrr_30d != null ? pct(data.nrr_30d * 100) : '—'}
              fx={data.nrr_30d != null ? 'MRR ÷ snapshot ≥28d old' : 'snapshots collecting — needs 28d of history'} />
            <Kpi eyebrow="LTV / CAC" tag="gap" value="—" fx="no acquisition-spend source" />
          </div>
          <Panel title="MRR · daily" tag={(data.mrr_history || []).length > 1 ? 'live' : 'gap'}
            cap={(data.mrr_history || []).length > 1 ? 'Daily 00:05 UTC snapshots (mrr_snapshot), last 90 days.' : 'Daily snapshots started recording at deploy — the trend draws itself from here.'}>
            {(data.mrr_history || []).length > 1 ? (
              <div className="cd-chart-body" style={{ height: 180 }}>
                <Line
                  data={{
                    labels: data.mrr_history.map((h) => h.date.slice(5).replace('-', '/')),
                    datasets: [{ label: 'MRR', data: data.mrr_history.map((h) => h.mrr_usd), borderColor: (CHART_THEME[theme] || CHART_THEME.light).s1, backgroundColor: (CHART_THEME[theme] || CHART_THEME.light).s1, tension: 0.3, pointRadius: 0, borderWidth: 2 }],
                  }}
                  options={chartOpts(CHART_THEME[theme] || CHART_THEME.light, { money: true })}
                />
              </div>
            ) : (
              <div style={{ padding: '14px 15px', fontSize: 12.5, color: 'var(--cd-muted)' }}>
                {(data.mrr_history || []).length === 1 ? 'First snapshot recorded — one more day and the line begins.' : 'No snapshots yet.'}
              </div>
            )}
          </Panel>
          <div className="cd-note"><b>Forecast is trend-ready.</b> MRR snapshots now record daily — NRR lights up after 28 days of history. The one remaining blocker for LTV / CAC is an acquisition-spend feed.</div>
        </>
      )}

      {/* Team metrics is hidden for now.
      {mode === 'business' && tab === 'aa' && (
        <>
          <div className="cd-kpis">
            <Kpi eyebrow="Team" tag="live" value={data.team?.size || 0} sub="AA staff" fx="user.account_type='staff'" />
            <Kpi eyebrow="Live sessions" tag="live" value={data.sessions?.live || 0} sub={`${data.sessions?.impersonations || 0} acting-as`} />
            <Kpi eyebrow="Departments" tag="live" value={data.team?.departments ?? 0} sub="routing fabric" fx="department ⋈ internal client" />
            <Kpi eyebrow="Expert utilization" tag="gap" value="—" fx="needs capacity model" />
          </div>
          <div className="cd-note">Departments, experts and team-leads are managed on <button className="cd-link" style={{ display: 'inline' }} onClick={() => navigate('/super/team-overview')}>Agents Anywhere</button>. This tab will surface their capacity and utilization once the count queries are wired in.</div>
        </>
      )}
      */}

      {/* ═══════════════ MACHINE ═══════════════ */}
      {mode === 'machine' && tab === 'overview' && (
        <>
          {/* ── Money first (CTO 10-12): today / 30d / all-time, trusted band,
              and what that spend becomes at the Compass margin dial. ── */}
          {bm.available && bm.spend ? (
            <div className="cd-kpis">
              <Kpi eyebrow="Spent today" tag="live" value={usd2(bm.spend.today?.total_usd)}
                sub={`${usd2(bm.spend.today?.llm_usd)} LLM · ${usd2(bm.spend.today?.compute_usd)} compute`}
                fx="trusted band · UTC day" />
              <Kpi eyebrow="Spent · 30d" tag="live" value={usd2(bm.spend.d30?.total_usd)}
                sub={`${usd2(bm.spend.d30?.llm_usd)} LLM · ${usd2(bm.spend.d30?.compute_usd)} compute`}
                fx="trusted LLM + compute" />
              <Kpi eyebrow="All-time spend" tag="live" value={usd2(bm.spend.all_time?.total_usd)}
                sub={`${usd2(bm.spend.all_time?.llm_usd)} LLM · ${usd2(bm.spend.all_time?.compute_usd)} compute`} />
              {typeof bm.spend.billed_usd === 'number' ? (
                <Kpi eyebrow="Charged · 30d" tag="live" vclass="good"
                  value={usd2(bm.spend.billed_usd)}
                  sub={`${usd2(bm.spend.billed_usd - (bm.spend.d30?.total_usd || 0))} earned margin`}
                  fx="sell price frozen on each usage row" />
              ) : bm.spend.margin_percent > 0 ? (
                <Kpi eyebrow={`Charged at +${bm.spend.margin_percent}% · 30d`} tag="live" vclass="good"
                  value={usd2((bm.spend.d30?.total_usd || 0) * (1 + bm.spend.margin_percent / 100))}
                  sub={`${usd2((bm.spend.d30?.total_usd || 0) * (bm.spend.margin_percent / 100))} earned margin`}
                  fx="Compass margin dial × trusted 30d spend" />
              ) : (
                <Kpi eyebrow="Margin dial" tag="est" value="at cost"
                  sub="0% — set it on Compass to see charged/earned"
                  fx="platform_config.platformMarginPercent" />
              )}
            </div>
          ) : (
            <div className="cd-kpis">
              <Kpi eyebrow="LLM spend · 30d" tag={bm.available ? 'live' : 'gap'} value={bm.available ? usd2(bm.totals?.llm_cost_usd) : '—'} sub="trusted, open-rate priced" fx="Σ estimatedCostUsd, costUntrusted=false" />
              <Kpi eyebrow="Compute · 30d" tag={bm.available ? 'live' : 'gap'} value={bm.available ? usd2(bm.totals?.compute_cost_usd) : '—'} sub="E2B box-seconds at cost" fx="Σ compute_usage.totalCostUsd" />
              <Kpi eyebrow="Live boxes" tag={bm.available ? 'live' : 'gap'} value={bm.available ? (bm.boxes?.running ?? 0) : '—'} vclass="accent" sub={bm.available ? `${bm.boxes?.paused ?? 0} paused · ${usd2(bm.boxes?.cost_usd)}/h` : ''} />
              <Kpi eyebrow="Untrusted spend" tag="est" value={bm.available ? usd2(bm.totals?.untrusted_usd) : '—'} vclass="red" sub="data-quality, not real money" fx="Σ where costUntrusted=true" />
            </div>
          )}
          <ProjectsNow theme={theme} />
          {bm.available && bm.spend && (
            <div className="cd-kpis">
              <Kpi eyebrow="Shipped · 30d" tag="live" value={bm.delivery?.tickets_shipped_30d ?? 0}
                sub={`${bm.delivery?.prs_merged_30d ?? 0} PRs merged · ${bm.delivery?.tickets_open ?? 0} open`} />
              <Kpi eyebrow="Cost per shipped item" tag="live"
                value={bm.delivery?.cost_per_ticket_usd != null ? usd2(bm.delivery.cost_per_ticket_usd) : '—'}
                fx="30d trusted spend ÷ shipped tickets" />
              <Kpi eyebrow="Live boxes" tag="live" value={bm.boxes?.running ?? 0} vclass="accent"
                sub={`${bm.boxes?.paused ?? 0} paused · ${usd2(bm.boxes?.cost_usd)}/h`} />
              <Kpi eyebrow="Untrusted band" tag="est" value={usd2(bm.spend.d30?.untrusted_usd)} vclass="red"
                sub="unpriced at run time — data quality, not real money" fx="Σ where costUntrusted=true · 30d" />
            </div>
          )}
          {bm.available && bm.routing && (
            <Panel title="Compass routing" tag="live"
              right={<button className="cd-link" onClick={() => navigate('/super/machine/compass')}>Compass <ArrowUpRight size={13} /></button>}
              cap="Every AA Router decision, straight off the decision ledger: live vs shadow, actually applied vs lane fallback, what the judging itself cost.">
              <table className="cd-t">
                <thead><tr><th></th><th className="r">Decisions</th><th className="r">Live</th><th className="r">Applied</th><th className="r">Fallback</th><th className="r">Judge latency</th><th className="r">Judge cost</th></tr></thead>
                <tbody>
                  {[['Today', bm.routing.today], ['30 days', bm.routing.d30], ['All time', bm.routing.all_time]].map(([label, r]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td className="r m">{r?.decisions ?? 0}</td>
                      <td className="r m">{r?.live ?? 0}</td>
                      <td className="r m">{r?.applied ?? 0}</td>
                      <td className="r m">{r?.fallback ? <span style={{ color: 'var(--cd-warn)' }}>{r.fallback}</span> : 0}</td>
                      <td className="r m">{r?.avg_latency_ms != null ? `${r.avg_latency_ms}ms` : '—'}</td>
                      <td className="r m">{usd2(r?.jev_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
          {!bm.available ? (
            <div className="cd-note">The builder metrics endpoint is unreachable ({bm.reason || 'unknown'}). Engine spend, boxes, and providers appear once the builder is reachable from aibot with platform-metrics.</div>
          ) : (
            <div className="cd-grid2">
              <Panel title="Spend by model · 30d" tag="est"
                right={<button className="cd-link" onClick={() => navigate('/super/machine/cost')}>Cost detail <ArrowUpRight size={13} /></button>}
                cap="Model stores the raw engine spec (:default), not the resolved model — real models collapse. Fix on Data Health.">
                <table className="cd-t"><thead><tr><th>Model</th><th>Provider</th><th className="r">Cost</th></tr></thead><tbody>
                  {(bm.by_model || []).slice(0, 10).map((m, i) => (
                    <tr key={i}><td className="m">{m.model}</td><td><span className="cd-sub2">{m.provider}</span></td><td className="r m">{usd2(m.cost_usd)}</td></tr>
                  ))}
                  {(bm.by_model || []).length === 0 && <tr><td className="cd-sub2" colSpan={3}>No spend in window.</td></tr>}
                </tbody></table>
              </Panel>
              <Panel title="Providers" tag="live"
                right={<span style={{ display: 'flex', gap: 10 }}>
                  <button className="cd-link" onClick={() => navigate('/super/machine/compass')}>AA Compass &amp; Swarm value <ArrowUpRight size={13} /></button>
                  <button className="cd-link" onClick={() => navigate('/super/machine/routing')}>Manage <ArrowUpRight size={13} /></button>
                </span>}>
                <table className="cd-t"><tbody>
                  {(bm.providers || []).map((p, i) => (
                    <tr key={i}><td><span className={`cd-hd ${p.active ? 'g' : p.enabled ? 'a' : 'r'}`} />{p.label || p.key}<div className="cd-sub2">{(p.lanes || []).join(', ')}</div></td>
                      <td className="r"><span className={`cd-chip ${p.active ? 'g' : 'o'}`}>{p.active ? 'active' : p.enabled ? 'enabled' : 'off'}</span></td></tr>
                  ))}
                  {(bm.providers || []).length === 0 && <tr><td className="cd-sub2">No providers.</td></tr>}
                </tbody></table>
              </Panel>
            </div>
          )}
          <div className="cd-foot">builder <span className="cd-mono">/api/provisioning/platform-metrics</span> · service-key, read-only</div>
        </>
      )}

      {mode === 'machine' && ['routing', 'models', 'seat', 'keys', 'access', 'general', 'cost'].includes(tab) && (
        <div className="cd-tabs" role="tablist" aria-label="Settings">
          {/* ['access', 'Access'], ['cost', 'Controls'] */}
          {[['routing', 'Agents'], ['models', 'Swarm'], ['seat', 'Seat'], ['keys', 'Keys'], ['general', 'General']].map(([t, label]) => (
            <button key={t} className="cd-tab" data-on={tab === t} onClick={() => navigate(`/super/machine/${t}`)}>{label}</button>
          ))}
        </div>
      )}

      {mode === 'machine' && MACHINE_SETTINGS_TABS.includes(tab) && <MachineSettingsTab tab={tab} />}

      {/*
      {mode === 'machine' && tab === 'cost' && (
        <>
          <div className="cd-kpis">
            <Kpi eyebrow="LLM · 30d" tag={bm.available ? 'live' : 'gap'} value={bm.available ? usd2(bm.totals?.llm_cost_usd) : '—'} fx="trusted spend" />
            <Kpi eyebrow="Compute · 30d" tag={bm.available ? 'live' : 'gap'} value={bm.available ? usd2(bm.totals?.compute_cost_usd) : '—'} fx="E2B at cost" />
            <Kpi eyebrow="Spent today" tag="live" value={bm.available && bm.spend ? usd2(bm.spend.today?.total_usd) : '—'}
              sub={bm.spend ? `${usd2(bm.spend.today?.llm_usd)} LLM · ${usd2(bm.spend.today?.compute_usd)} compute` : ''} fx="trusted band · UTC day" />
            <Kpi eyebrow="Margin dial" tag="live" value={bm.spend ? `${bm.spend.margin_percent}%` : '—'}
              sub={bm.spend?.margin_percent > 0 ? 'default sell = cost × (1 + margin)' : 'selling at cost'} fx="one pricing authority — per-model sell overrides it" />
          </div>
          <div className="cd-grid2 even">
            <Panel title="Spend by feature" tag={bm.available ? 'live' : 'gap'}
              cap="Where the 30d LLM spend went, by spend category.">
              <table className="cd-t"><tbody>
                {(bm.by_lane || []).map((l, i) => (
                  <tr key={i}><td>{l.lane || l.feature}</td><td className="r m">{usd2(l.cost_usd)}</td></tr>
                ))}
                {(bm.by_lane || []).length === 0 && <tr><td className="cd-sub2" colSpan={2}>{bm.available ? 'No spend in window.' : 'Builder unavailable.'}</td></tr>}
              </tbody></table>
            </Panel>
            <ControlsPanel key={String(surf.cost?.data?.controls?.executionCapUsd30d ?? 'unset')} st={surf.cost} onWrite={writeControls} />
          </div>
        </>
      )}
      */}

      {mode === 'machine' && tab === 'delivery' && (
        <>
          <div className="cd-kpis">
            <Kpi eyebrow="Tickets shipped · 30d" tag={bm.available ? 'live' : 'gap'} value={bm.available ? (bm.delivery?.tickets_shipped_30d ?? 0) : '—'} vclass="good" fx="status=COMPLETED, completedAt" />
            <Kpi eyebrow="PRs merged · 30d" tag={bm.available ? 'live' : 'gap'} value={bm.available ? (bm.delivery?.prs_merged_30d ?? 0) : '—'} fx="PullRequest status=MERGED" />
            <Kpi eyebrow="Cost / shipped ticket" tag={bm.available && bm.delivery?.cost_per_ticket_usd != null ? 'est' : 'gap'} value={bm.available && bm.delivery?.cost_per_ticket_usd != null ? usd2(bm.delivery.cost_per_ticket_usd) : '—'} vclass="accent" fx="(LLM + compute) ÷ tickets shipped" />
            <Kpi eyebrow="LOC shipped · 30d" tag="gap" value="—" fx="project-level JSON; per-ticket needs cols" />
          </div>
          <div className="cd-grid2 even">
            <Panel title="Work in flight" tag={bm.available ? 'live' : 'gap'}>
              <table className="cd-t"><tbody>
                <tr><td>Open tickets</td><td className="r m">{bm.available ? (bm.delivery?.tickets_open ?? 0) : '—'}</td></tr>
                <tr><td>Shipped · 30d</td><td className="r m" style={{ color: 'var(--cd-good)' }}>{bm.available ? (bm.delivery?.tickets_shipped_30d ?? 0) : '—'}</td></tr>
                <tr><td>PRs merged · 30d</td><td className="r m">{bm.available ? (bm.delivery?.prs_merged_30d ?? 0) : '—'}</td></tr>
              </tbody></table>
            </Panel>
            <Panel title="Run quality" tag={bm.available && bm.delivery?.runs_30d > 0 ? 'live' : 'gap'}
              cap={bm.available && bm.delivery?.runs_30d > 0
                ? 'From the append-only TicketRun history — every execution attempt counts.'
                : 'TicketRun instrumentation is live; numbers appear as runs record post-deploy.'}>
              <table className="cd-t"><tbody>
                <tr><td>Runs · 30d</td><td className="r m">{bm.available ? (bm.delivery?.runs_30d ?? 0) : '—'}</td></tr>
                <tr><td>First-pass rate</td><td className="r m" style={{ color: 'var(--cd-good)' }}>{bm.available && bm.delivery?.first_pass_rate != null ? pct(bm.delivery.first_pass_rate * 100) : '—'}</td></tr>
                <tr><td>Rework runs · 30d</td><td className="r m" style={{ color: bm.delivery?.rework_runs_30d ? 'var(--cd-warn)' : undefined }}>{bm.available && bm.delivery?.rework_runs_30d != null ? bm.delivery.rework_runs_30d : '—'}</td></tr>
                <tr><td>Cost per run</td><td className="r m">{bm.available && bm.delivery?.cost_per_run_usd != null ? usd2(bm.delivery.cost_per_run_usd) : '—'}</td></tr>
              </tbody></table>
            </Panel>
          </div>
          <div className="cd-note"><b>Run history is now recorded.</b> Every execution attempt writes an append-only <span className="cd-mono">TicketRun</span> row (outcome, engine, model, cost, duration, files) — first-pass vs rework and per-run cost are computed from it. Still open: LOC per ticket (project-level only) and per-expert attribution (one engineer per project today) — sequenced on Data Health.</div>
        </>
      )}

      {/*
      {mode === 'machine' && tab === 'access' && (
        <Panel title="Access — who may use what" tag="live"
          cap="Workspace-member and expert access are governed PER WORKSPACE (the same gates the builder's Access tab edits). Open a project and use 'Open in builder' to manage its workspace's members; expert assignment lives on the Experts page here.">
          <div className="cd-sub2" style={{ padding: '4px 0' }}>
            Platform-wide access levers that live on this deck: expert assignment (Experts page), per-project pause and delete (Projects), the Seat switch, and provider enablement (Keys). Per-workspace member gates are scoped to each client workspace by design — there is no global override, on purpose.
          </div>
        </Panel>
      )}
      */}

      {mode === 'machine' && tab === 'general' && <GeneralTab />}


      {mode === 'machine' && (tab === 'swarm' || tab === 'compass') && (
        <SurfaceShell st={surf[tab]}>{(d) => {
          if (d.available === false) return <BuilderDown reason={d.reason} />;
          const t = CHART_THEME[theme] || CHART_THEME.light;
          const mix = d.model_mix || [];
          const funding = d.funding_split || [];
          const judg = d.judgment_distribution || [];
          const bucket = d.swarm_bucket || {};
          const quality = d.quality_by_model || [];
          const realJudg = judg.filter((j) => j.synthetic === false);
          const synthJudg = judg.filter((j) => j.synthetic === true);
          const mixLabels = mix.map((m) => `${m.model} · ${m.provider}`);
          const fundingLabels = funding.map((f) => f.funding_source || 'unattributed');
          return (<>
            <CompassDecisions />
            <div className="cd-note">
              <b>Does the swarm + AA Compass routing actually pay off?</b> Model mix and funding split are real usage rows
              (<span className="cd-mono">AiRuntimeUsage</span>), windowed by <span className="cd-mono">?days=</span>.
              Judgment distribution splits <b>real</b> (content-policy = structured/full) from <b>synthetic</b>
              (fabricated placeholder, TypeSafe's own content-policy safety default) — a synthetic row is never
              evidence AA Compass judged anything real. Quality-by-model excludes QA runs that died to infra, not defects.
            </div>
            <div className="cd-kpis">
              <Kpi eyebrow="Active swarm host" tag="live" value={bucket.active_host?.label || '—'} vclass="accent" fx="InferenceProvider kind=open-host, isActive" />
              <Kpi eyebrow="Open models servable" tag="live" value={bucket.open_models_enabled ?? 0} sub={`${bucket.open_models_priced ?? 0} priced · ${bucket.open_models_unpriced ?? 0} unpriced`} />
              <Kpi eyebrow="Real Compass judgments" tag={realJudg.length ? 'live' : 'gap'} value={realJudg.reduce((s, j) => s + j.count, 0)} vclass={realJudg.length ? 'good' : 'red'} sub={`${synthJudg.reduce((s, j) => s + j.count, 0)} synthetic (not evidence)`} />
              <Kpi eyebrow="Live routing influenced" tag="live" value={`${d.live_routing?.influenced ?? 0} / ${d.live_routing?.armed ?? 0}`} sub="armed calls that actually changed the served model" />
            </div>
            <div className="cd-grid2">
              <Panel title="Model mix by runs" tag="live" cap="Count of real AiRuntimeUsage rows per model+provider, this window.">
                <div className="cd-chart-body" style={{ height: 200 }}>
                  <Bar
                    data={{ labels: mixLabels, datasets: [{ data: mix.map((m) => m.runs), backgroundColor: t.s1, borderRadius: 3, maxBarThickness: 22 }] }}
                    options={chartOpts(t)}
                  />
                </div>
              </Panel>
              <Panel title="Cost by funding source" tag="live" cap="incurred_cost_usd = what AA is actually on the hook for (zero when customer-funded via their own seat/key).">
                <div className="cd-chart-body" style={{ height: 200 }}>
                  <Bar
                    data={{ labels: fundingLabels, datasets: [{ data: funding.map((f) => f.incurred_cost_usd || 0), backgroundColor: t.s2, borderRadius: 3, maxBarThickness: 22 }] }}
                    options={chartOpts(t, { money: true })}
                  />
                </div>
              </Panel>
            </div>
            <Panel title="AA Compass judgment distribution" tag="live"
              cap="wouldHave.action from the routing-policy layer — 'uncertain' dominating means AA Compass isn't confidently deciding anything yet (expected under a synthetic content policy; watch this shift once real content flows).">
              <table className="cd-t">
                <thead><tr><th>Lane</th><th>Boundary</th><th>Would-have</th><th>Content</th><th className="r">Count</th></tr></thead>
                <tbody>
                  {judg.map((j, i) => (
                    <tr key={i}>
                      <td>{j.lane}</td><td className="cd-sub2">{j.step_kind}</td><td>{j.action || '—'}</td>
                      <td><span className={`cd-chip ${j.synthetic ? 'o' : 'g'}`}>{j.synthetic ? 'synthetic' : 'real'}</span></td>
                      <td className="r m">{j.count}</td>
                    </tr>
                  ))}
                  {judg.length === 0 && <tr><td className="cd-sub2" colSpan={5}>No step-level judgments in this window.</td></tr>}
                </tbody>
              </table>
            </Panel>
            <Panel title="QA pass rate by model" tag="live" cap="Excludes QaRun rows whose status='failed' (the run died to infra, not a QA verdict) — per the routing-quality research's own recommendation.">
              <table className="cd-t">
                <thead><tr><th>Model</th><th>Provider</th><th className="r">Tickets w/ QA</th><th className="r">Passed</th><th className="r">Failed</th><th className="r">Pass rate</th></tr></thead>
                <tbody>
                  {quality.map((q, i) => (
                    <tr key={i}>
                      <td className="m">{q.model}</td><td><span className="cd-sub2">{q.provider}</span></td>
                      <td className="r m">{q.tickets_with_qa}</td><td className="r m">{q.passed}</td><td className="r m">{q.failed}</td>
                      <td className="r m">{q.pass_rate != null ? `${Math.round(q.pass_rate * 100)}%` : '—'}</td>
                    </tr>
                  ))}
                  {quality.length === 0 && <tr><td className="cd-sub2" colSpan={6}>No QA-correlated builds in this window.</td></tr>}
                </tbody>
              </table>
            </Panel>
            <div className="cd-foot">builder <span className="cd-mono">GET /api/provisioning/admin/jev-swarm-summary</span> · service-key passthrough · window {d.window_days}d</div>
          </>);
        }}</SurfaceShell>
      )}

      {mode === 'machine' && tab === 'compute' && (
        <>
          <div className="cd-kpis">
            <Kpi eyebrow="Running" tag={bm.available ? 'live' : 'gap'} value={bm.available ? (bm.boxes?.running ?? 0) : '—'} vclass="accent" />
            <Kpi eyebrow="Paused" tag={bm.available ? 'live' : 'gap'} value={bm.available ? (bm.boxes?.paused ?? 0) : '—'} sub="free · 30d retention" />
            <Kpi eyebrow="Burn" tag={bm.available ? 'live' : 'gap'} value={bm.available ? `${usd2(bm.boxes?.cost_usd)}/h` : '—'} />
            <Kpi eyebrow="Per-run box↔LLM" tag="gap" value="—" fx="compute_usage has no runId" />
          </div>
          <div className="cd-note">A live sandbox list with pause / resume / kill needs a per-box passthrough on platform-metrics (the backend actions exist: containers/manage + reconcile cron). Sequenced on Data Health.</div>
        </>
      )}

      {/* ── Projects — the lens ── */}
      {mode === 'machine' && tab === 'projects' && (
        <SurfaceShell st={surf.projects}>{(d) => {
          if (d.available === false) return <BuilderDown reason={d.reason} />;
          return <ProjectsLens d={d} theme={theme} openBuilder={openBuilder} projectId={projectId}
            openProject={(id) => navigate(`/super/machine/projects/${id}`)}
            closeProject={() => navigate('/super/machine/projects')} />;
        }}</SurfaceShell>
      )}

      {/* ── Experts ── */}
      {mode === 'machine' && tab === 'people' && (
        <SurfaceShell st={surf.people}>{(d) => {
          if (d.available === false) return <BuilderDown reason={d.reason} />;
          const xs = d.experts || [];
          const approved = xs.filter((e) => e.status === 'APPROVED').length;
          const activeProjects = xs.reduce((s, e) => s + (e.activeProjects || 0), 0);
          const openTier = xs.filter((e) => !(e.allowedModels || []).length).length;
          return (<>
            <div className="cd-kpis">
              <Kpi eyebrow="Experts" tag="live" value={xs.length} vclass="accent" fx="builder Expert rows" />
              <Kpi eyebrow="Approved" tag="live" value={approved} sub={xs.length - approved ? `${xs.length - approved} in another status` : 'everyone'} />
              <Kpi eyebrow="Active projects" tag="live" value={activeProjects} sub="not completed / cancelled" fx="Σ per-expert open projects" />
              <Kpi eyebrow="Open-tier only" tag="live" value={openTier} sub="no flagship allowlist" fx="allowedModels = []" />
            </div>
            <ExpertAssign experts={xs} />
            <Panel title="Roster" tag="live"
              cap="Assignment is live here (above). Approve/reject + per-expert model allowlists still edit on builder admin until their provisioning twins land.">
              <table className="cd-t">
                <thead><tr><th>Expert</th><th>Status</th><th>Expertise</th><th>Models</th><th className="r">Active</th><th className="r">Resolved · 30d</th><th className="r">Added</th></tr></thead>
                <tbody>
                  {xs.map((e) => (
                    <tr key={e.id}>
                      <td>{e.name}<div className="cd-sub2">{e.email || '—'}</div></td>
                      <td><span className={`cd-chip ${e.status === 'APPROVED' ? 'g' : 'w'}`}>{(e.status || '').toLowerCase()}</span></td>
                      <td className="cd-sub2">{(e.expertise || []).join(', ') || '—'}</td>
                      <td>{(e.allowedModels || []).length
                        ? <span className="cd-chip b">{e.allowedModels.length} allowed</span>
                        : <span className="cd-chip o">open-tier</span>}</td>
                      <td className="r m">{e.activeProjects || 0}</td>
                      <td className="r m" style={{ color: e.resolved_30d ? 'var(--cd-good)' : undefined }}>{e.resolved_30d ?? 0}</td>
                      <td className="r m cd-sub2">{e.addedAt ? new Date(e.addedAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                  {xs.length === 0 && <tr><td className="cd-sub2" colSpan={7}>No experts yet.</td></tr>}
                </tbody>
              </table>
            </Panel>
            <div className="cd-note">Resolved · 30d counts tickets this expert answered (<span className="cd-mono">resolvedByExpertId</span>, stamped at the expert-response routes). Per-expert LOC still needs per-ticket LOC — named on Data Health.</div>
          </>);
        }}</SurfaceShell>
      )}

      {/* ── Capabilities ── */}
      {mode === 'machine' && tab === 'caps' && (
        <SurfaceShell st={surf.caps}>{(d) => {
          if (d.available === false) return <BuilderDown reason={d.reason} />;
          const items = [...(d.items || [])].sort((a, b) => (b.installCount || 0) - (a.installCount || 0));
          const alwaysOn = items.filter((i) => i.alwaysOn).length;
          const partner = items.filter((i) => i.origin === 'partner').length;
          const installs = items.reduce((s, i) => s + (i.installCount || 0), 0);
          return (<>
            <div className="cd-kpis">
              <Kpi eyebrow="Catalog items" tag="live" value={items.length} vclass="accent" fx="builtins + PLATFORM + partner" />
              <Kpi eyebrow="Always-on" tag="live" value={alwaysOn} sub="resolved for every project" />
              <Kpi eyebrow="Partner-published" tag="live" value={partner} sub="PUBLIC visibility" />
              <Kpi eyebrow="Installs" tag="live" value={installs} sub="Σ across the catalog" />
            </div>
            <Panel title="The shared shelf" tag="live"
              cap="Sorted by installs. Platform-owned rows can be made always-on, removed, or (if adopted from a compiled-in built-in) promoted into a real catalog entry."
              right={(
                <div style={{ display: 'flex', gap: 7 }}>
                  <button className="cd-btn sm" onClick={() => { setCapCreateForm({ name: '', description: '', instructions: '', category: '' }); setCapCreate('skill'); }}><Plus size={13} /> Create skill</button>
                  <button className="cd-btn sm" onClick={() => { setCapCreateForm({ name: '', description: '', instructions: '', category: '' }); setCapCreate('plugin'); }}><Plus size={13} /> Create plugin</button>
                  <button className="cd-btn sm pri" onClick={() => { setCapImportState({ mode: 'github', source: '', file: null, busy: false, items: null, selected: {} }); setCapImportOpen(true); }}><Github size={13} /> Import</button>
                </div>
              )}>
              <table className="cd-t">
                <thead><tr><th>Capability</th><th>Kind</th><th>Origin</th><th>Category</th><th className="r">Installs</th><th className="r">Flags</th><th className="r">Actions</th></tr></thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td>{i.name}<div className="cd-sub2">{i.description ? `${i.description.slice(0, 90)}${i.description.length > 90 ? '…' : ''}` : i.slug}</div></td>
                      <td><span className={`cd-chip ${i.kind === 'plugin' ? 'p' : i.kind === 'template' ? 'b' : 'o'}`}>{i.kind}</span></td>
                      <td className="cd-sub2">{i.origin === 'agents_anywhere' ? 'Agents Anywhere' : i.origin}{i.sourceType === 'builtin' ? ' · builtin' : ''}</td>
                      <td className="cd-sub2">{i.category || '—'}</td>
                      <td className="r m">{i.installCount || 0}</td>
                      <td className="r">
                        {i.alwaysOn && <span className="cd-chip g">always-on</span>}
                        {!i.hasSkillMd && i.sourceType !== 'builtin' && <span className="cd-chip w" title="No SKILL.md — enabling resolves to nothing">no skill.md</span>}
                      </td>
                      <td className="r" style={{ whiteSpace: 'nowrap' }}>
                        {i.sourceType === 'builtin' ? (
                          <button className="cd-btn sm" onClick={() => saveCapability({ action: 'adopt', builtinId: i.id }).then(() => toast.success(`${i.name} adopted into the catalog`))}>Adopt</button>
                        ) : (
                          <>
                            {i.origin === 'agents_anywhere' && (
                              <button className="cd-btn sm" onClick={() => saveCapability({ action: 'always-on', capabilityId: i.id, enabled: !i.alwaysOn }).then(() => toast.success(i.alwaysOn ? `${i.name} is no longer always-on` : `${i.name} is now always-on`))}>{i.alwaysOn ? 'Unset always-on' : 'Make always-on'}</button>
                            )}{' '}
                            <button className="cd-btn sm" style={{ color: 'var(--cd-red)' }} onClick={() => { if (window.confirm(`Delete "${i.name}" from the shared catalog? This removes it for every client in validx and aibot.`)) saveCapability({ action: 'delete', capabilityId: i.id }).then(() => toast.success(`${i.name} deleted`)); }}>Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td className="cd-sub2" colSpan={7}>Catalog is empty.</td></tr>}
                </tbody>
              </table>
            </Panel>
          </>);
        }}</SurfaceShell>
      )}

      {/* ── Platform ── */}
      {mode === 'machine' && tab === 'platform' && (
        <SurfaceShell st={surf.platform}>{(d) => (
          <>
            {d.available === false && <BuilderDown reason={d.reason} />}
            {d.available !== false && (
              <div className="cd-grid2 even">
                <Panel title="Inference providers" tag="live"
                  cap="Credentials masked — the deck only ever sees whether a key is set. Edits (keys, lanes, active flips) stay on the builder admin until CD3 writes.">
                  <table className="cd-t">
                    <thead><tr><th>Provider</th><th>Billing</th><th>Lanes</th><th className="r">Key</th><th className="r">State</th></tr></thead>
                    <tbody>
                      {(d.providers || []).map((p) => (
                        <tr key={p.key}>
                          <td><span className={`cd-hd ${p.isActive ? 'g' : p.enabled ? 'a' : 'r'}`} />{p.label || p.key}<div className="cd-sub2">{p.kind}</div></td>
                          <td className="cd-sub2">{p.billingMode === 'subscription' ? `subscription${p.monthlyPriceUsd != null ? ` · ${usd(p.monthlyPriceUsd)}/mo` : ''}` : 'per-token'}</td>
                          <td className="cd-sub2">{(p.lanes || []).join(', ') || '—'}</td>
                          <td className="r">{p.hasKey ? <span className="cd-chip g">set</span> : <span className="cd-chip w">none</span>}</td>
                          <td className="r"><span className={`cd-chip ${p.isActive ? 'g' : 'o'}`}>{p.isActive ? 'active' : p.enabled ? 'enabled' : 'off'}</span></td>
                        </tr>
                      ))}
                      {(d.providers || []).length === 0 && <tr><td className="cd-sub2" colSpan={5}>No providers.</td></tr>}
                    </tbody>
                  </table>
                </Panel>
                <Panel title="Lane routing" tag="live"
                  cap="LaneConfig is the authority: provider + default model + engine per lane. null = today's default behavior (never-brick).">
                  <table className="cd-t">
                    <thead><tr><th>Lane</th><th>Provider</th><th>Fallback model</th><th className="r">Engine</th></tr></thead>
                    <tbody>
                      {(d.lanes || []).map((l) => (
                        <tr key={l.lane}>
                          <td className="m">{l.lane}</td>
                          <td className="cd-sub2">{l.providerKey || 'default'}</td>
                          <td className="cd-sub2 m">{l.defaultModel || '—'}</td>
                          <td className="r"><span className="cd-chip o">{l.engine || 'claude-code'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
                <Panel title="Slack notification routing" tag="live">
                  <table className="cd-t"><tbody>
                    {(d.channels || []).map((c) => (
                      <tr key={c.type}>
                        <td>{c.label}<div className="cd-sub2 cd-mono">{c.type}</div></td>
                        <td className="r">{c.connected
                          ? <span className="cd-chip g">#{c.channelName}{c.teamName ? ` · ${c.teamName}` : ''}</span>
                          : <span className="cd-chip w">not connected</span>}</td>
                      </tr>
                    ))}
                    {(d.channels || []).length === 0 && <tr><td className="cd-sub2">No channel types.</td></tr>}
                  </tbody></table>
                </Panel>
                <Panel title="Workbench policy" tag="live" cap="Org-level box controls — team-login toggles + open-tier availability.">
                  <table className="cd-t"><tbody>
                    <tr><td>Team login · Anthropic</td><td className="r"><span className={`cd-chip ${d.workbench?.teamLogin?.anthropic ? 'g' : 'o'}`}>{d.workbench?.teamLogin?.anthropic ? 'on' : 'off'}</span></td></tr>
                    <tr><td>Team login · Codex</td><td className="r"><span className={`cd-chip ${d.workbench?.teamLogin?.codex ? 'g' : 'o'}`}>{d.workbench?.teamLogin?.codex ? 'on' : 'off'}</span></td></tr>
                    <tr><td>Open models available</td><td className="r"><span className={`cd-chip ${d.workbench?.openModels ? 'g' : 'w'}`}>{d.workbench?.openModels ? 'yes' : 'no'}</span></td></tr>
                  </tbody></table>
                </Panel>
              </div>
            )}
            <Panel title="aibot platform settings" tag="live"
              cap="platform_settings table → env → default. Edited on Plans & Payments / super settings (PUT /api/super/platform-settings).">
              <table className="cd-t"><tbody>
                <tr><td>Solution-partner booking</td><td className="r m cd-sub2">{d.aibot_settings?.solution_partner_cal_url || '—'}</td></tr>
                <tr><td>Solution-partner email</td><td className="r m cd-sub2">{d.aibot_settings?.solution_partner_email || '—'}</td></tr>
                <tr><td>Deep-scoping price</td><td className="r m">{d.aibot_settings?.deep_scoping_price_cents != null ? usd(d.aibot_settings.deep_scoping_price_cents / 100) : '—'}</td></tr>
                <tr><td>Plugin download access</td><td className="r"><span className="cd-chip o">{String(d.aibot_settings?.plugin_download_access || 'super')}</span>{Array.isArray(d.aibot_settings?.plugin_download_allowed_emails) && d.aibot_settings.plugin_download_allowed_emails.length > 0 && <span className="cd-sub2"> · {d.aibot_settings.plugin_download_allowed_emails.length} allowed</span>}</td></tr>
              </tbody></table>
            </Panel>
          </>
        )}</SurfaceShell>
      )}

      {/* ── Audit ── */}
      {/* MARGIN (CTO 10-10) — the owner's one dial and one honest view of
          value: true cost (what the models + boxes actually cost us) and the
          with-margin value once a margin is set. Zero today = sell at cost.
          The dial lives in builder cost-controls (audited, no redeploy). */}
      {/*
      {mode === 'machine' && tab === 'margin' && (() => {
        const st = surf.margin;
        const ctr = st?.data?.controls || {};
        const marginPct = Number(ctr.platformMarginPercent || 0);
        const llm30 = Number(bm?.totals?.llm_cost_usd || 0);
        const compute30 = Number(bm?.totals?.compute_cost_usd || 0);
        const trueCost = llm30 + compute30;
        const billed = bm?.spend?.billed_usd;
        let withMargin = trueCost * (1 + marginPct / 100);
        if (typeof billed === 'number') withMargin = billed;
        return (
          <>
            <div className="cd-note">
              <b>One dial, applied on top of true cost.</b> True cost is what the models AA Compass picked
              and the workspaces actually cost us (per-call receipts + box seconds, at cost). The margin
              percent is added ON TOP wherever value is shown — it never changes what the ledger records.
              Model-level sell overrides on the Pricing tab still win where set. Today the dial is
              {marginPct === 0 ? ' ZERO — we sell at cost.' : ` ${marginPct}%.`}
            </div>
            <div className="cd-kpis">
              <Kpi eyebrow="True cost · 30d" value={usd2(trueCost)} sub={`${usd2(llm30)} models · ${usd2(compute30)} compute`} fx="per-call receipts + box seconds, at cost" tag={bm?.available ? 'live' : 'gap'} />
              <Kpi eyebrow="Margin" value={`${marginPct}%`} sub={marginPct === 0 ? 'selling at cost' : 'applied on top of true cost'} tag="live" />
              <Kpi eyebrow="With margin · 30d" value={usd2(withMargin)} sub={marginPct === 0 ? 'equals true cost' : `${usd2(withMargin - trueCost)} value on top`} tag="live" />
              <Kpi eyebrow="Value created · 30d" value={usd2(withMargin - trueCost)} sub={marginPct === 0 ? 'set a margin to see value' : 'margin × true cost'} tag={marginPct === 0 ? 'gap' : 'live'} />
            </div>
            <div className="cd-grid2">
              <MarginDial st={st} onWrite={writeControls} />
              <Panel title="Where the margin applies" tag="live">
                <table className="cd-t"><tbody>
                  <tr><td>Model calls (LLM)<div className="cd-sub2">every call AA Compass routes — cost from frozen per-call rates</div></td><td className="r m">{usd2(llm30)} → {usd2(llm30 * (1 + marginPct / 100))}</td></tr>
                  <tr><td>Compute (workspaces)<div className="cd-sub2">E2B box seconds, billed at cost today</div></td><td className="r m">{usd2(compute30)} → {usd2(compute30 * (1 + marginPct / 100))}</td></tr>
                  <tr><td><b>Total · 30d</b></td><td className="r m"><b>{usd2(trueCost)} → {usd2(withMargin)}</b></td></tr>
                </tbody></table>
              </Panel>
            </div>
          </>
        );
      })()}
      */}
      {mode === 'machine' && tab === 'audit' && (
        <SurfaceShell st={surf.audit}>{(d) => {
          if (d.available === false) return <BuilderDown reason={d.reason} />;
          const events = d.events || [];
          return (<>
            <div className="cd-note"><b>Coverage is partial by design of what writes today.</b> Provider + model ops append <span className="cd-mono">AdminAuditEvent</span> rows; lane edits, expert/customer changes and future kill-switch flips still need coverage — sequenced with CD3 writes. Details carry at most a last-4 fingerprint, never a credential.</div>
            <Panel title="Admin audit trail" tag="live" cap="Newest first. Actor resolved server-side; keyset-paged.">
              <table className="cd-t">
                <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Subject</th><th>Detail</th></tr></thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id}>
                      <td className="m cd-sub2" style={{ whiteSpace: 'nowrap' }}>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                      <td>{e.actor?.name || e.actor?.email || <span className="cd-sub2">unknown</span>}{e.actor?.name && e.actor?.email && <div className="cd-sub2">{e.actor.email}</div>}</td>
                      <td><span className="cd-chip b">{e.action}</span></td>
                      <td className="m">{e.subject}</td>
                      <td className="cd-sub2 m" style={{ fontSize: 10.5 }}>{e.detail ? JSON.stringify(e.detail).slice(0, 90) : '—'}</td>
                    </tr>
                  ))}
                  {events.length === 0 && <tr><td className="cd-sub2" colSpan={5}>No audit events yet.</td></tr>}
                </tbody>
              </table>
            </Panel>
            {d.next_before && (
              <button className="cd-btn" onClick={loadMoreAudit} disabled={surf.audit?.more}>
                {surf.audit?.more ? 'Loading…' : 'Load older events'}
              </button>
            )}
          </>);
        }}</SurfaceShell>
      )}

      {mode === 'machine' && tab === 'data' && (
        <>
          <div className="cd-note"><b>The deck's honesty contract.</b> Nothing is faked. Where a number is an estimate or missing, the deck says so and names the one change that fixes it — most are a single column or a small table.</div>
          <Panel title="Instrumentation backlog">
            <table className="cd-t"><thead><tr><th>Gap</th><th>Unlocks</th><th className="r">Effort</th></tr></thead><tbody>
              {[
                ['No charge.refunded handler', 'refunds netted out of recognized revenue', 'S'],
                ['Provider mislabel + model collapses to :default', 'honest cost by provider/model; kills phantom', 'S'],
                ['Per-ticket expert attribution (one engineer/project)', 'true per-engineer leaderboard', 'M'],
                ['LOC only project-level in JSON', 'LOC per ticket / engine / expert', 'S'],
                ['No cache token counts stored', 'cache-hit % and $ recovery', 'S'],
                ['No acquisition-spend (CAC) source', 'LTV, CAC (NRR now snapshots daily)', 'M'],
                ['Engine tok/s only in Langfuse', 'engine-lane speed in-deck (intake runtime is live)', 'M'],
                ['Expert/customer admin changes unaudited', 'full governance trail (lanes/providers/controls now audit)', 'S'],
              ].map((r, i) => (
                <tr key={i}><td>{r[0]}</td><td className="cd-sub2">{r[1]}</td><td className="r"><span className={`cd-chip ${r[2] === 'S' ? 'g' : 'w'}`}>{r[2]}</span></td></tr>
              ))}
            </tbody></table>
          </Panel>
        </>
      )}

      {/* account drawer */}
      {drawer && (
        <>
          <div className="cd-drawer-scrim" onClick={() => setDrawer(null)} />
          <div className="cd-drawer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="cd-eyebrow">Account P&amp;L</div>
              <div className="cd-btn sm" onClick={() => setDrawer(null)} style={{ padding: '4px 8px' }}><X size={14} /></div>
            </div>
            <h2 style={{ margin: '4px 0 2px', fontSize: 19, letterSpacing: '-.02em' }}>{drawer.name || drawer.email}</h2>
            <div className="cd-sub2">{drawer.email}</div>
            <div className="cd-kpis" style={{ gridTemplateColumns: '1fr 1fr', margin: '16px 0' }}>
              <Kpi eyebrow="Rev · 30d" value={usd(drawer.revenue_usd)} />
              <Kpi eyebrow="True cost" value={usd2(drawer.cogs_usd)} />
              <Kpi eyebrow="Contribution" value={usd2(drawer.margin_usd)} vclass={(drawer.margin_usd || 0) < 0 ? 'red' : 'good'} />
              <Kpi eyebrow="Margin" value={drawer.margin_pct == null ? '—' : pct(drawer.margin_pct)} vclass="good" />
            </div>
            <Panel title="The pennies behind the cost" tag="live">
              <table className="cd-t"><tbody>
                <tr><td>LLM + compute (builder)</td><td className="r m">{usd2(drawer.builder_cost_usd)}</td></tr>
                <tr><td>aibot chat</td><td className="r m">{usd2(drawer.chat_cost_usd)}</td></tr>
                <tr><td><b>Total COGS</b></td><td className="r m"><b>{usd2(drawer.cogs_usd)}</b></td></tr>
              </tbody></table>
            </Panel>
            <Panel title="Reconciled (nightly, persisted)" tag={recon?.data?.entries?.[0] ? 'live' : 'gap'}
              cap={!recon?.data?.entries?.length ? 'No nightly reconciliation row yet for this account — either the job hasn’t run since this account got a paid/trial subscription, or it has no resolvable stripe customer id.' : undefined}>
              {recon?.loading ? (
                <div style={{ padding: '12px 15px', fontSize: 12.5, color: 'var(--cd-muted)' }}>Loading…</div>
              ) : recon?.error ? (
                <div style={{ padding: '12px 15px', fontSize: 12.5, color: 'var(--cd-red)' }}>{recon.error}</div>
              ) : recon?.data?.entries?.[0] ? (
                <table className="cd-t"><tbody>
                  <tr><td>aibot (token_usage)</td><td className="r m">{usd2(recon.data.entries[0].aibot_cost_usd)}</td></tr>
                  <tr><td>builder (AiRuntimeUsage){!recon.data.entries[0].builder_available && ' — unavailable at reconcile time'}</td><td className="r m">{usd2(recon.data.entries[0].builder_cost_usd)}</td></tr>
                  <tr><td><b>True total</b></td><td className="r m"><b>{usd2(recon.data.entries[0].total_cost_usd)}</b></td></tr>
                  <tr><td className="cd-sub2">window</td><td className="r cd-sub2">{recon.data.entries[0].window_start?.slice(0, 10)} → {recon.data.entries[0].window_end?.slice(0, 10)}</td></tr>
                  <tr><td className="cd-sub2">ticket-run cross-check</td><td className="r cd-sub2">{usd2(recon.data.entries[0].builder_ticket_run_cost_usd)}</td></tr>
                </tbody></table>
              ) : (
                <div style={{ padding: '12px 15px', fontSize: 12.5, color: 'var(--cd-muted)' }}>Nothing reconciled yet.</div>
              )}
            </Panel>
            <Panel title="Delivered" tag="gap"
              cap="Tickets / PRs / LOC per account need the delivery passthrough + instrumentation.">
              <div style={{ padding: '12px 15px', fontSize: 12.5, color: 'var(--cd-muted)' }}>Wire from builder tickets + PullRequest once the delivery passthrough lands.</div>
            </Panel>
            <button className="cd-btn pri" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} onClick={() => openBuilder(drawer.id)}>Open workspace <ArrowUpRight size={14} /></button>
          </div>
        </>
      )}

      {/* Create capability drawer */}
      {capCreate && (
        <>
          <div className="cd-drawer-scrim" onClick={() => !capCreateBusy && setCapCreate(null)} />
          <div className="cd-drawer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="cd-eyebrow">Create {capCreate}</div>
              <div className="cd-btn sm" onClick={() => !capCreateBusy && setCapCreate(null)} style={{ padding: '4px 8px' }}><X size={14} /></div>
            </div>
            <h2 style={{ margin: '4px 0 2px', fontSize: 19, letterSpacing: '-.02em' }}>New platform {capCreate}</h2>
            <div className="cd-sub2">Visible PLATFORM-wide — every client's Agents Anywhere tab, in validx and in aibot.</div>

            <label className="cd-label">Name</label>
            <input className="cd-input" value={capCreateForm.name} onChange={(e) => setCapCreateForm((f) => ({ ...f, name: e.target.value }))} placeholder={capCreate === 'plugin' ? 'e.g. Slack Notifier' : 'e.g. Weekly Status Writer'} />

            <label className="cd-label">Description</label>
            <input className="cd-input" value={capCreateForm.description} onChange={(e) => setCapCreateForm((f) => ({ ...f, description: e.target.value }))} placeholder="One line — what it does and when the engine should reach for it" />

            <label className="cd-label">Category</label>
            <input className="cd-input" value={capCreateForm.category} onChange={(e) => setCapCreateForm((f) => ({ ...f, category: e.target.value }))} placeholder="optional" />

            <label className="cd-label">Instructions (SKILL.md body)</label>
            <textarea className="cd-textarea" value={capCreateForm.instructions} onChange={(e) => setCapCreateForm((f) => ({ ...f, instructions: e.target.value }))} placeholder="The full instructions the engine reads when this is active…" />

            <button
              className="cd-btn pri"
              style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
              disabled={capCreateBusy || !capCreateForm.name.trim() || !capCreateForm.instructions.trim()}
              onClick={async () => {
                setCapCreateBusy(true);
                try {
                  await saveCapability({ action: 'create', kind: capCreate, ...capCreateForm });
                  toast.success(`${capCreateForm.name} created`);
                  setCapCreate(null);
                } catch { /* toasted in saveCapability */ }
                finally { setCapCreateBusy(false); }
              }}
            >
              {capCreateBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create {capCreate}
            </button>
          </div>
        </>
      )}

      {/* Import capability drawer — GitHub repo or .zip upload */}
      {capImportOpen && (
        <>
          <div className="cd-drawer-scrim" onClick={() => !capImportState.busy && setCapImportOpen(false)} />
          <div className="cd-drawer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="cd-eyebrow">Import capability</div>
              <div className="cd-btn sm" onClick={() => !capImportState.busy && setCapImportOpen(false)} style={{ padding: '4px 8px' }}><X size={14} /></div>
            </div>
            <h2 style={{ margin: '4px 0 2px', fontSize: 19, letterSpacing: '-.02em' }}>Bring in a skill or plugin</h2>
            <div className="cd-sub2">Inspect a source, pick which SKILL.md/plugin folders to bring in — imported items are PLATFORM-visible, same as a manual create.</div>

            <div className="cd-modes" style={{ marginTop: 12 }}>
              <button className="cd-mode" data-on={capImportState.mode === 'github'} onClick={() => setCapImportState((s) => ({ mode: 'github', source: '', file: null, busy: false, items: null, selected: {} }))}><Github size={13} /> GitHub repo</button>
              <button className="cd-mode" data-on={capImportState.mode === 'zip'} onClick={() => setCapImportState((s) => ({ mode: 'zip', source: '', file: null, busy: false, items: null, selected: {} }))}>Upload .zip</button>
            </div>

            {capImportState.mode === 'github' ? (
              <>
                <label className="cd-label">Repository (owner/repo, or full URL)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="cd-input"
                    value={capImportState.source}
                    onChange={(e) => setCapImportState((s) => ({ ...s, source: e.target.value }))}
                    placeholder="e.g. agents-anywhere/skills-pack"
                    onKeyDown={(e) => { if (e.key === 'Enter') document.getElementById('cd-cap-inspect-btn')?.click(); }}
                  />
                  <button
                    id="cd-cap-inspect-btn"
                    className="cd-btn sm"
                    disabled={capImportState.busy || !capImportState.source.trim()}
                    onClick={async () => {
                      setCapImportState((s) => ({ ...s, busy: true, items: null }));
                      try {
                        const r = await importCapability({ action: 'inspect', source: capImportState.source.trim() });
                        const items = r.items || [];
                        const selected = {};
                        for (const it of items) selected[it.key] = true;
                        setCapImportState((s) => ({ ...s, busy: false, items, selected }));
                      } catch {
                        setCapImportState((s) => ({ ...s, busy: false }));
                      }
                    }}
                  >
                    {capImportState.busy ? <Loader2 size={13} className="animate-spin" /> : 'Inspect'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <label className="cd-label">.zip file</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="cd-input"
                    type="file"
                    accept=".zip"
                    onChange={(e) => setCapImportState((s) => ({ ...s, file: e.target.files?.[0] || null, items: null }))}
                  />
                  <button
                    className="cd-btn sm"
                    disabled={capImportState.busy || !capImportState.file}
                    onClick={async () => {
                      setCapImportState((s) => ({ ...s, busy: true, items: null }));
                      try {
                        const r = await sendDeckCapabilitiesZipImport('inspect', capImportState.file);
                        const items = r.items || [];
                        const selected = {};
                        for (const it of items) selected[it.key] = true;
                        setCapImportState((s) => ({ ...s, busy: false, items, selected }));
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || 'Inspect failed');
                        setCapImportState((s) => ({ ...s, busy: false }));
                      }
                    }}
                  >
                    {capImportState.busy ? <Loader2 size={13} className="animate-spin" /> : 'Inspect'}
                  </button>
                </div>
              </>
            )}

            {capImportState.items && (
              <>
                <label className="cd-label">Found {capImportState.items.length} item(s) — pick which to import</label>
                <div style={{ border: '1px solid var(--cd-line)', borderRadius: 8, padding: '2px 10px', maxHeight: 260, overflowY: 'auto' }}>
                  {capImportState.items.map((it) => (
                    <div key={it.key} className="cd-check-row">
                      <input
                        type="checkbox"
                        checked={!!capImportState.selected[it.key]}
                        onChange={(e) => setCapImportState((s) => ({ ...s, selected: { ...s.selected, [it.key]: e.target.checked } }))}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{it.name || it.key}</div>
                        <div className="cd-sub2">{it.type}{it.description ? ` · ${it.description.slice(0, 70)}` : ''}</div>
                      </div>
                    </div>
                  ))}
                  {capImportState.items.length === 0 && <div className="cd-sub2" style={{ padding: '10px 0' }}>Nothing importable found at that source.</div>}
                </div>

                <button
                  className="cd-btn pri"
                  style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}
                  disabled={capImportState.busy || !Object.values(capImportState.selected).some(Boolean)}
                  onClick={async () => {
                    const keys = Object.keys(capImportState.selected).filter((k) => capImportState.selected[k]);
                    setCapImportState((s) => ({ ...s, busy: true }));
                    try {
                      const r = capImportState.mode === 'github'
                        ? await importCapability({ action: 'import', source: capImportState.source.trim(), keys })
                        : await sendDeckCapabilitiesZipImport('import', capImportState.file, keys);
                      toast.success(`Imported ${r.imported?.length ?? keys.length} item(s)`);
                      reloadSurface('caps');
                      setCapImportOpen(false);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Import failed');
                      setCapImportState((s) => ({ ...s, busy: false }));
                    }
                  }}
                >
                  {capImportState.busy ? <Loader2 size={14} className="animate-spin" /> : <Github size={14} />} Import selected
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CommandDeck;
